import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiTrash2, FiChevronRight, FiChevronLeft, FiChevronDown, FiLoader, FiCheck,
  FiAlertTriangle, FiPlay, FiFileText, FiUsers, FiSearch,
  FiUser, FiClipboard, FiDownload, FiEdit2, FiX, FiEye,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import { isGuideSeen, markGuideSeen } from '../utils/onboarding';
import ConsentModal from '../components/ConsentModal';
import './GrantAgent.css';

const GA_STAGE_HINTS = {
  nofo_input: "Paste or upload your NOFO/RFP. This tells the system what the funder is looking for.",
  funder_research: "Analyzing the funding landscape. This runs automatically.",
  pi_profile: "Confirm your PI profile \u2014 expertise, grants, and team. This shapes the proposal voice.",
  team_building: "Suggesting collaborators based on your profile and NOFO requirements.",
  drafting: "Generating your proposal draft with real citations.",
  panel_review: "Six reviewers are reading your draft. Accept or decline each issue, then click Iterate Draft to auto-fix. Save and re-run the review to keep improving.",
  compliance: "Checking your draft against NOFO requirements.",
  done: "Your proposal is ready. Use the tabs to edit the draft, review feedback, or check compliance.",
};

const STAGES = [
  { key: 'nofo_input',       label: 'NOFO',       type: 'human' },
  { key: 'funder_research',  label: 'Research',   type: 'auto'  },
  { key: 'pi_profile',       label: 'PI Profile', type: 'human' },
  { key: 'team_building',    label: 'Team',       type: 'auto'  },
  { key: 'drafting',         label: 'Draft',      type: 'auto', loop: 'start' },
  { key: 'panel_review',     label: 'Panel Review', type: 'auto', loop: 'end'   },
  { key: 'compliance',       label: 'Compliance', type: 'auto'  },
  { key: 'done',             label: 'Done',       type: null    },
];

const STAGE_ICONS = {
  nofo_input:      <FiFileText />,
  funder_research: <FiSearch />,
  pi_profile:      <FiUser />,
  team_building:   <FiUsers />,
  drafting:        <FiClipboard />,
  panel_review:    <FiEye />,
  compliance:      <FiCheck />,
  done:            <FiCheck />,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function stageIndex(key) {
  return STAGES.findIndex(s => s.key === key);
}

function stageState(proposal, stageKey) {
  const current = stageIndex(proposal.current_stage);
  const target  = stageIndex(stageKey);
  if (target < current) return 'done';
  if (target === current) return proposal.stage_status === 'done' ? 'done' : 'active';
  return 'pending';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StageStepper({ proposal, viewStage, onStageClick }) {
  const isDone = proposal.current_stage === 'done';
  // When current_stage is 'done', the Results step is the default view (viewStage === null)
  const resultsActive = isDone && viewStage === null;

  return (
    <div className="ga-stepper">
      {STAGES.filter(s => s.key !== 'done').map((s, i) => {
        const state = stageState(proposal, s.key);
        const isRunning = state === 'active' && proposal.stage_status === 'running';
        // A step is "viewing" if it's explicitly selected, or if it's the active stage and nothing is selected
        const isViewing = viewStage === s.key || (!viewStage && !isDone && s.key === proposal.current_stage);
        const isClickable = state === 'done' || s.key === proposal.current_stage;
        const loopClass = s.loop ? ` ga-step--loop-${s.loop}` : '';
        const connectorIsLoop = s.loop === 'start';
        return (
          <React.Fragment key={s.key}>
            <div
              className={`ga-step ga-step--${state}${isRunning ? ' ga-step--running' : ''}${isViewing ? ' ga-step--viewing' : ''}${loopClass}`}
              onClick={isClickable ? () => onStageClick(viewStage === s.key ? null : s.key) : undefined}
              style={isClickable ? { cursor: 'pointer' } : {}}
            >
              <div className="ga-step-icon">
                {state === 'done' ? <FiCheck /> : isRunning ? <FiLoader className="spin" /> : STAGE_ICONS[s.key]}
              </div>
              <span className="ga-step-label">{s.label}</span>
              {s.type && <span className="ga-step-type">{s.type === 'auto' ? 'auto' : 'you'}</span>}
            </div>
            {i < STAGES.length - 2 && (
              <div className={`ga-step-connector${state === 'done' ? ' ga-step-connector--done' : ''}${connectorIsLoop ? ' ga-step-connector--loop' : ''}`}>
                {connectorIsLoop && (
                  <span className="ga-loop-arrow" title="Iterate: review feedback rewrites the draft">
                    ⟲ iterate
                  </span>
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}

      {/* Results step — only shown when the whole workflow is complete */}
      {isDone && (
        <React.Fragment>
          <div className="ga-step-connector ga-step-connector--done" />
          <div
            className={`ga-step ga-step--done${resultsActive ? ' ga-step--viewing' : ''}`}
            onClick={() => onStageClick(null)}
            style={{ cursor: 'pointer' }}
          >
            <div className="ga-step-icon"><FiFileText /></div>
            <span className="ga-step-label">Results</span>
            <span className="ga-step-type">outputs</span>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

function AutoStageShell({ title, description, status, onRun, onReset, readOnly, children }) {
  // Auto-start when the stage becomes active — no manual Run click needed
  useEffect(() => {
    if (!readOnly && status === 'pending') {
      onRun();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ga-auto-stage">
      <div className="ga-auto-stage-header">
        <div>
          <h3>{title}</h3>
          <p className="ga-stage-desc">{description}</p>
        </div>
        {(status === 'pending' || status === 'running') && !readOnly && (
          <span className="ga-running-badge"><FiLoader className="ga-spin" /> Working…</span>
        )}
        {status === 'done' && (
          <div className="ga-done-header">
            <span className="ga-done-badge"><FiCheck /> Complete</span>
            {!readOnly && onReset && (
              <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={onReset} title="Re-run this stage">
                <FiPlay /> Re-run
              </button>
            )}
          </div>
        )}
      </div>
      {(status === 'pending' || status === 'running') && (
        <div className="ga-stage-placeholder ga-stage-placeholder--loading">
          <FiLoader className="ga-spin" /> Working…
        </div>
      )}
      {status === 'done' && children}
    </div>
  );
}

// ── Stage renderers ───────────────────────────────────────────────────────────

function NofoInputStage({ proposal, onAdvance, readOnly, rewindMode = false, collections = [], initialCollectionId = null, initialIdea = null }) {
  const draftKey = `ga_draft_nofo_${proposal.id}`;
  const [nofoUrl, setNofoUrl] = useState(proposal.nofo_url || '');
  const [nofoText, setNofoText] = useState(() => {
    if (proposal.nofo_text) return proposal.nofo_text; // already submitted
    try { return localStorage.getItem(draftKey) || ''; } catch { return ''; }
  });
  const [ideaBrief] = useState(() => {
    if (!initialIdea) return '';
    const papers = (initialIdea.supporting_papers || []).map(p => `[${p.citation_key}] ${p.title}`).join('\n');
    return `Research Direction (from Mnevia collection analysis)\n` +
      `Title: ${initialIdea.title}\n` +
      `Gap: ${initialIdea.gap}\n` +
      `Background: ${initialIdea.description}` +
      (papers ? `\nKey papers:\n${papers}` : '');
  });
  const [selectedCollectionId, setSelectedCollectionId] = useState(
    proposal.collection_id || initialCollectionId || ''
  );
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  useEffect(() => {
    if (!proposal.nofo_text) {
      try { localStorage.setItem(draftKey, nofoText); } catch {}
    }
  }, [nofoText]); // eslint-disable-line react-hooks/exhaustive-deps
  const pdfRef = React.useRef(null);

  const handlePdfUpload = async (file) => {
    setUploading(true);
    setUploadMsg('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/upload-nofo`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: form,
      });
      if (!res.ok) {
        let detail = 'Upload failed';
        try { detail = (await res.json()).detail || detail; } catch { detail = (await res.text()) || detail; }
        throw new Error(detail);
      }
      const updated = await res.json();
      setNofoText(updated.nofo_text || '');
      setUploadMsg(`✓ Extracted ${(updated.nofo_text || '').length.toLocaleString()} characters from PDF`);
    } catch (e) {
      setUploadMsg(`Error: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ga-human-stage">
      <h3>NOFO / RFP Details</h3>
      <p className="ga-stage-desc">
        Provide the funding opportunity. Upload the PDF or paste the URL and text.
        The more text you provide, the better the automated research will be.
      </p>
      {proposal.nofo_number && (
        <div className="ga-nofo-prefilled-banner">
          ✓ NOFO pre-loaded from idea discovery — {proposal.nofo_number}
          {proposal.idea_title && <span className="ga-nofo-prefilled-idea"> · "{proposal.idea_title}"</span>}
        </div>
      )}
      {ideaBrief && (
        <div className="ga-idea-brief">
          <div className="ga-idea-brief-label">Research direction from collection analysis</div>
          <pre className="ga-idea-brief-text">{ideaBrief}</pre>
        </div>
      )}
      <div className="ga-form-group">
        <label>
          Reference Collection <span className="ga-label-hint">(optional — grounds citations in real papers)</span>
        </label>
        <select
          value={selectedCollectionId}
          onChange={e => setSelectedCollectionId(e.target.value)}
          disabled={readOnly}
          className="ga-collection-select"
        >
          <option value="">— No collection —</option>
          {collections.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}{c.item_count ? ` (${c.item_count} papers)` : ''}
            </option>
          ))}
        </select>
      </div>
      <div className="ga-form-group">
        <label>NOFO URL</label>
        <input
          type="url"
          placeholder="https://grants.gov/..."
          value={nofoUrl}
          onChange={e => setNofoUrl(e.target.value)}
          disabled={readOnly}
        />
      </div>
      <div className="ga-form-group">
        <div className="ga-nofo-text-header">
          <label>NOFO Text <span className="ga-label-hint">(paste or upload PDF)</span></label>
          {!readOnly && (
            <>
              <input
                ref={pdfRef}
                type="file"
                accept=".pdf,application/pdf"
                className="ga-upload-input"
                onChange={e => { if (e.target.files[0]) handlePdfUpload(e.target.files[0]); e.target.value = ''; }}
              />
              <button
                className="ga-btn ga-btn--outline ga-btn--sm"
                onClick={() => pdfRef.current?.click()}
                disabled={uploading}
              >
                <FiDownload style={{ transform: 'rotate(180deg)' }} />
                {uploading ? 'Extracting…' : 'Upload PDF'}
              </button>
            </>
          )}
        </div>
        {uploadMsg && (
          <div className={`ga-upload-msg${uploadMsg.startsWith('✓') ? ' ga-upload-msg--ok' : ' ga-upload-msg--err'}`}>
            {uploadMsg}
          </div>
        )}
        <textarea
          rows={10}
          placeholder="Paste the NOFO text here, or upload a PDF above…"
          value={nofoText}
          onChange={e => setNofoText(e.target.value)}
          disabled={readOnly}
        />
      </div>
      {!readOnly && (
        <div className="ga-stage-actions">
          <button
            className="ga-btn ga-btn--primary"
            disabled={!nofoUrl && !nofoText && !ideaBrief}
            onClick={() => {
            try { localStorage.removeItem(draftKey); } catch {}
            const combinedText = ideaBrief && nofoText
              ? `${ideaBrief}\n\n---\n\n${nofoText}`
              : ideaBrief || nofoText;
            onAdvance({
              nofo_url: nofoUrl,
              nofo_text: combinedText,
              collection_id: selectedCollectionId || null,
            });
          }}
          >
            {rewindMode ? 'Save & Re-run from here' : 'Continue'} <FiChevronRight />
          </button>
        </div>
      )}
    </div>
  );
}

function FunderResearchStage({ proposal, onRun, onReset, onAdvance, readOnly, onProposalUpdate }) {
  const r = proposal.funder_research;
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({
    program_name: proposal.program_name || '',
    agency: proposal.agency || '',
    grant_mechanism: proposal.grant_mechanism || '',
  });
  const [savingMeta, setSavingMeta] = useState(false);
  const [calibrating, setCalibrating] = useState(false);
  const [criteria, setCriteria] = useState(() => {
    const rc = proposal.review_criteria;
    return rc ? rc.criteria || [] : [];
  });
  const [criteriaSource, setCriteriaSource] = useState(() => {
    const rc = proposal.review_criteria;
    return rc ? rc.calibration_source : null;
  });
  const [editingCriteria, setEditingCriteria] = useState(false);
  const [savingCriteria, setSavingCriteria] = useState(false);
  const [refSet, setRefSet] = useState(null);
  const [pullingRefs, setPullingRefs] = useState(false);
  const [showRefSet, setShowRefSet] = useState(false);

  // Load reference set on mount
  useEffect(() => {
    if (!proposal.id) return;
    fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/reference-set`), {
      headers: getAuthHeaders(),
    }).then(r => r.ok ? r.json() : null).then(data => {
      if (data) setRefSet(data);
    }).catch(() => {});
  }, [proposal.id]);

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/metadata`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(metaForm),
      });
      if (res.ok && onProposalUpdate) {
        const data = await res.json();
        onProposalUpdate(data);
      }
      setEditingMeta(false);
    } catch (e) { console.error(e); }
    setSavingMeta(false);
  };

  const runCalibration = async () => {
    setCalibrating(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/calibrate-criteria`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const rc = data.criteria;
        setCriteria(rc.criteria || []);
        setCriteriaSource(rc.calibration_source);
        if (onProposalUpdate) onProposalUpdate(data.proposal);
      }
    } catch (e) { console.error(e); }
    setCalibrating(false);
  };

  const updateCriterion = (idx, field, value) => {
    setCriteria(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    setEditingCriteria(true);
  };

  const removeCriterion = (idx) => {
    setCriteria(prev => prev.filter((_, i) => i !== idx));
    setEditingCriteria(true);
  };

  const addCriterion = () => {
    setCriteria(prev => [...prev, {
      id: `custom_${Date.now()}`,
      label: 'New Criterion',
      weight: 'medium',
      focus: '',
      source: 'user',
    }]);
    setEditingCriteria(true);
  };

  const saveCriteria = async () => {
    setSavingCriteria(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/review-criteria`), {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ criteria }),
      });
      if (res.ok && onProposalUpdate) {
        const data = await res.json();
        onProposalUpdate(data);
        setCriteriaSource('user_edited');
        setEditingCriteria(false);
      }
    } catch (e) { console.error(e); }
    setSavingCriteria(false);
  };

  const autoPullRefs = async () => {
    setPullingRefs(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/reference-set/auto-pull`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        // Reload reference set
        const rsRes = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/reference-set`), {
          headers: getAuthHeaders(),
        });
        if (rsRes.ok) setRefSet(await rsRes.json());
      }
    } catch (e) { console.error(e); }
    setPullingRefs(false);
  };

  const toggleRefExclude = async (refSetId, currentlyExcluded) => {
    try {
      const form = new FormData();
      form.append('excluded', currentlyExcluded ? 'false' : 'true');
      await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/reference-set/${refSetId}`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: form,
      });
      // Reload
      const rsRes = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/reference-set`), {
        headers: getAuthHeaders(),
      });
      if (rsRes.ok) setRefSet(await rsRes.json());
    } catch (e) { console.error(e); }
  };

  const WEIGHT_COLORS = { high: '#dc2626', medium: '#d97706', low: '#6b7280' };

  return (
    <AutoStageShell
      title="Funder Research"
      description="The agent researches the funder's vision, admin priorities, evaluation criteria, and EO risks."
      status={proposal.stage_status}
      onRun={onRun}
      onReset={onReset}
      readOnly={readOnly}
    >
      {r && (
        <div className="ga-research-results">
          {/* ── Program Metadata Bar ──────────────────────── */}
          <div className="ga-result-card ga-result-card--full" style={{ background: '#f0f9ff', borderColor: '#bae6fd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>Program Metadata</h4>
              {!readOnly && !editingMeta && (
                <button className="ga-btn ga-btn--sm" onClick={() => setEditingMeta(true)}>
                  <FiEdit2 /> Edit
                </button>
              )}
            </div>
            {editingMeta ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Program Name
                  <input className="ga-input" value={metaForm.program_name}
                    onChange={e => setMetaForm(f => ({ ...f, program_name: e.target.value }))}
                    placeholder="e.g. Safe Streets and Roads for All (SS4A)" />
                </label>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Agency
                  <input className="ga-input" value={metaForm.agency}
                    onChange={e => setMetaForm(f => ({ ...f, agency: e.target.value }))}
                    placeholder="e.g. DOT, NIH, NSF" />
                </label>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Grant Mechanism
                  <input className="ga-input" value={metaForm.grant_mechanism}
                    onChange={e => setMetaForm(f => ({ ...f, grant_mechanism: e.target.value }))}
                    placeholder="e.g. Cooperative Agreement, R01" />
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={saveMeta} disabled={savingMeta}>
                    {savingMeta ? <><FiLoader className="ga-spin" /> Saving...</> : 'Save'}
                  </button>
                  <button className="ga-btn ga-btn--sm" onClick={() => setEditingMeta(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span className="ga-tag ga-tag--blue">{proposal.program_name || metaForm.program_name || 'No program'}</span>
                <span className="ga-tag" style={{ background: '#e0e7ff', color: '#3730a3' }}>{proposal.agency || metaForm.agency || 'No agency'}</span>
                <span className="ga-tag" style={{ background: '#f3e8ff', color: '#6b21a8' }}>{proposal.grant_mechanism || metaForm.grant_mechanism || 'No mechanism'}</span>
              </div>
            )}
          </div>

          <div className="ga-result-card">
            <h4>Funder</h4>
            <p>{r.funder_name}</p>
          </div>
          <div className="ga-result-card">
            <h4>Administration Keywords to Use Verbatim</h4>
            <div className="ga-tags">
              {(r.admin_keywords || []).map(k => <span key={k} className="ga-tag ga-tag--blue">{k}</span>)}
            </div>
          </div>
          <div className="ga-result-card">
            <h4>Evaluation Criteria (from Funder Research)</h4>
            <ol className="ga-criteria-list">
              {(r.key_criteria || []).map(c => <li key={c}>{c}</li>)}
            </ol>
          </div>
          <div className="ga-result-card ga-result-card--warn">
            <h4><FiAlertTriangle /> EO Risks to Avoid</h4>
            <ul>
              {(r.eo_risks || []).map(e => <li key={e}>{e}</li>)}
            </ul>
          </div>
          <div className="ga-result-card ga-result-card--full">
            <h4>Summary</h4>
            <p>{r.summary}</p>
          </div>

          {/* ── Reference Set Manager ────────────────────── */}
          <div className="ga-result-card ga-result-card--full" style={{ background: '#fefce8', borderColor: '#fde68a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>
                Reference Set
                {refSet && <span style={{ fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
                  ({refSet.included} included of {refSet.total})
                </span>}
              </h4>
              <div style={{ display: 'flex', gap: 8 }}>
                {!readOnly && (
                  <button className="ga-btn ga-btn--sm" onClick={autoPullRefs} disabled={pullingRefs}>
                    {pullingRefs ? <><FiLoader className="ga-spin" /> Pulling...</> : <><FiSearch /> Auto-Pull</>}
                  </button>
                )}
                <button className="ga-btn ga-btn--sm" onClick={() => setShowRefSet(!showRefSet)}>
                  {showRefSet ? 'Hide' : 'Show'} <FiChevronDown />
                </button>
              </div>
            </div>
            {showRefSet && refSet && refSet.reference_set.length > 0 && (
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {refSet.reference_set.map(ref => (
                  <div key={ref.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 8px', borderBottom: '1px solid #f3f4f6',
                    opacity: ref.excluded ? 0.5 : 1,
                  }}>
                    <div>
                      <span style={{
                        fontWeight: 600, fontSize: 12, marginRight: 6,
                        color: ref.outcome === 'funded' ? '#16a34a' : ref.outcome === 'rejected' ? '#dc2626' : '#6b7280',
                      }}>
                        {ref.outcome === 'funded' ? 'FUNDED' : ref.outcome === 'rejected' ? 'REJECTED' : ref.outcome?.toUpperCase() || '?'}
                      </span>
                      <span style={{ fontSize: 13 }}>
                        {ref.program_name || ref.funder || ''} {ref.grant_mechanism || ''}
                        {ref.impact_score ? ` (score: ${ref.impact_score})` : ''}
                      </span>
                    </div>
                    {!readOnly && (
                      <button
                        className="ga-btn ga-btn--sm"
                        onClick={() => toggleRefExclude(ref.id, ref.excluded)}
                        style={{ fontSize: 11 }}
                      >
                        {ref.excluded ? 'Include' : 'Exclude'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {showRefSet && (!refSet || refSet.reference_set.length === 0) && (
              <p style={{ color: '#6b7280', fontSize: 13, margin: '8px 0' }}>
                No reference proposals in set. Click Auto-Pull to find matching funded/rejected proposals.
              </p>
            )}
          </div>

          {/* ── Calibrated Review Criteria ────────────────── */}
          <div className="ga-result-card ga-result-card--full" style={{ background: '#f0fdf4', borderColor: '#a7f3d0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0 }}>
                Calibrated Review Criteria
                {criteriaSource && (
                  <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 8, color: '#6b7280' }}>
                    ({criteriaSource.replace(/_/g, ' ')})
                  </span>
                )}
              </h4>
              {!readOnly && (
                <button className="ga-btn ga-btn--sm" onClick={runCalibration} disabled={calibrating}>
                  {calibrating ? <><FiLoader className="ga-spin" /> Calibrating...</> : <><FiPlay /> {criteria.length ? 'Re-calibrate' : 'Calibrate from NOFO'}</>}
                </button>
              )}
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
              These criteria will be used by your panel reviewers instead of generic review criteria.
              {!readOnly && ' Edit them to match what you know about this program.'}
            </p>
            {criteria.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {criteria.map((c, i) => (
                  <div key={c.id || i} style={{
                    border: '1px solid #d1d5db', borderRadius: 8, padding: 12, background: '#fff',
                    borderLeft: `4px solid ${WEIGHT_COLORS[c.weight] || WEIGHT_COLORS.medium}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      {!readOnly ? (
                        <input
                          value={c.label} style={{ fontWeight: 600, fontSize: 14, border: 'none', borderBottom: '1px dashed #d1d5db', flex: 1 }}
                          onChange={e => updateCriterion(i, 'label', e.target.value)}
                        />
                      ) : (
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{c.label}</span>
                      )}
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {!readOnly ? (
                          <select value={c.weight} onChange={e => updateCriterion(i, 'weight', e.target.value)}
                            style={{ fontSize: 12, padding: '2px 6px', borderRadius: 4 }}>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                        ) : (
                          <span style={{ fontSize: 12, color: WEIGHT_COLORS[c.weight], fontWeight: 600 }}>{c.weight}</span>
                        )}
                        <span style={{ fontSize: 11, color: '#9ca3af', background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>
                          {c.source || 'nofo'}
                        </span>
                        {!readOnly && (
                          <button onClick={() => removeCriterion(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                            <FiX size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    {!readOnly ? (
                      <textarea
                        value={c.focus} rows={3}
                        onChange={e => updateCriterion(i, 'focus', e.target.value)}
                        style={{ width: '100%', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 4, padding: 6, resize: 'vertical' }}
                      />
                    ) : (
                      <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>{c.focus}</p>
                    )}
                  </div>
                ))}
                {!readOnly && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="ga-btn ga-btn--sm" onClick={addCriterion}>
                      <FiPlus /> Add Criterion
                    </button>
                    {editingCriteria && (
                      <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={saveCriteria} disabled={savingCriteria}>
                        {savingCriteria ? <><FiLoader className="ga-spin" /> Saving...</> : <><FiCheck /> Save Criteria</>}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p style={{ color: '#9ca3af', fontSize: 13 }}>
                No criteria calibrated yet. Click "Calibrate from NOFO" to generate program-specific review criteria.
              </p>
            )}
          </div>

          {!readOnly && (
            <div className="ga-stage-actions">
              <button className="ga-btn ga-btn--primary" onClick={() => onAdvance({})}>
                Looks good, continue <FiChevronRight />
              </button>
            </div>
          )}
        </div>
      )}
    </AutoStageShell>
  );
}

function PiProfileStage({ proposal, onAdvance, readOnly, rewindMode = false }) {
  const draftKey = `ga_draft_pi_${proposal.id}`;
  const saved = proposal.pi_profile || {};
  const EMPTY_PI = { pi_name: '', institution: '', expertise: '', prior_grants: '', homepage_url: '', team_assets: '', career_stage: '' };
  const [form, setForm] = useState(() => {
    if (saved.pi_name) return { ...EMPTY_PI, ...saved }; // already submitted
    try {
      const d = localStorage.getItem(draftKey);
      return d ? JSON.parse(d) : { ...EMPTY_PI, ...saved };
    } catch { return { ...EMPTY_PI, ...saved }; }
  });
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [fetchingGrants, setFetchingGrants] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchGrants = async () => {
    setFetchingGrants(true);
    try {
      const orcidMatch = (form.homepage_url || '').match(/\d{4}-\d{4}-\d{4}-\d{3}[\dX]/);
      const orcid = orcidMatch ? orcidMatch[0] : null;
      const params = new URLSearchParams();
      if (orcid) params.set('orcid', orcid);
      if (form.pi_name) params.set('name', form.pi_name);
      if (form.institution) params.set('institution', form.institution);
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/fetch-pi-grants?${params}`), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      if (data.grants_text) {
        set('prior_grants', data.grants_text);
      }
    } catch {
      // silently ignore — user can still type manually
    } finally {
      setFetchingGrants(false);
    }
  };

  useEffect(() => {
    if (!saved.pi_name) {
      try { localStorage.setItem(draftKey, JSON.stringify(form)); } catch {}
    }
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only pre-populate from saved profile if this proposal has no pi_profile yet
    if (saved.pi_name) return;
    fetch(getApiEndpoint('/api/grant-proposals/pi-profile'), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(profile => {
        if (!profile) return;
        const hasAnyField = Object.values(profile).some(v => v && typeof v === 'string' && v.trim());
        if (!hasAnyField) return;
        setForm(f => ({
          pi_name:      f.pi_name      || profile.pi_name      || '',
          institution:  f.institution  || profile.institution  || '',
          expertise:    f.expertise    || profile.expertise    || '',
          prior_grants: f.prior_grants || profile.prior_grants || '',
          homepage_url: f.homepage_url || profile.homepage_url || '',
          team_assets:  f.team_assets  || profile.team_assets  || '',
          career_stage: f.career_stage || profile.career_stage || '',
        }));
        setProfileLoaded(true);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ga-human-stage">
      <h3>PI & Team Profile</h3>
      <p className="ga-stage-desc">
        Tell the agent about you and your team. This determines the gap analysis
        and which consortium partners to seek.
      </p>
      {profileLoaded && (
        <p className="ga-profile-loaded-hint">
          <FiCheck /> Pre-filled from your saved PI profile — review and adjust as needed.
        </p>
      )}
      <div className="ga-form-row">
        <div className="ga-form-group">
          <label>PI Name <span className="ga-required">*</span></label>
          <input value={form.pi_name} onChange={e => set('pi_name', e.target.value)} placeholder="Dr. Jane Smith" />
        </div>
        <div className="ga-form-group">
          <label>Lead Institution <span className="ga-required">*</span></label>
          <input value={form.institution} onChange={e => set('institution', e.target.value)} placeholder="University of Utah" />
        </div>
      </div>
      {!readOnly && (!form.pi_name || !form.institution) && (
        <p className="ga-required-hint">* PI Name and Institution are required to continue.</p>
      )}
      <div className="ga-form-group">
        <label>Research Expertise</label>
        <textarea
          rows={3}
          value={form.expertise}
          onChange={e => set('expertise', e.target.value)}
          placeholder="Describe your primary research areas and technical strengths…"
        />
      </div>
      <div className="ga-form-group">
        <label>
          Prior Relevant Grants <span className="ga-label-hint">(name, agency, amount, years)</span>
          {!readOnly && (
            <button
              type="button"
              className="ga-fetch-grants-btn"
              onClick={fetchGrants}
              disabled={fetchingGrants || (!form.homepage_url && !form.pi_name)}
              title="Fetch funded grants from NIH, NSF, and ORCID using your name or ORCID"
            >
              {fetchingGrants ? <FiLoader className="spin" /> : <FiDownload />}
              {fetchingGrants ? ' Fetching…' : ' Fetch funded grants'}
            </button>
          )}
        </label>
        <textarea
          rows={4}
          value={form.prior_grants}
          onChange={e => set('prior_grants', e.target.value)}
          placeholder="NSF Award #1234567, $500K, 2022–2025 — Connected vehicle safety…"
        />
      </div>
      <div className="ga-form-group">
        <label>Existing Assets <span className="ga-label-hint">(labs, testbeds, datasets, industry relationships)</span></label>
        <textarea
          rows={3}
          value={form.team_assets}
          onChange={e => set('team_assets', e.target.value)}
          placeholder="Smart mobility testbed on campus; existing MOU with UDOT…"
        />
      </div>
      <div className="ga-form-group">
        <label>PI Homepage URL <span className="ga-label-hint">(optional — agent will fetch your profile)</span></label>
        <input
          type="url"
          value={form.homepage_url}
          onChange={e => set('homepage_url', e.target.value)}
          placeholder="https://faculty.utah.edu/..."
        />
      </div>
      <div className="ga-form-group">
        <label>Career Stage <span className="ga-label-hint">(shapes how the proposal is framed)</span></label>
        <select value={form.career_stage} onChange={e => set('career_stage', e.target.value)} className="ga-select">
          <option value="">— not specified —</option>
          <option value="esi">Early Stage Investigator (ESI) — ≤10 years post-PhD or first major award</option>
          <option value="established">Established Investigator — funded track record</option>
        </select>
        {form.career_stage === 'esi' && (
          <p className="ga-hint">Proposal will lead with feasibility and preliminary data.</p>
        )}
        {form.career_stage === 'established' && (
          <p className="ga-hint">Proposal will lead with paradigm shift and novelty.</p>
        )}
      </div>
      {!readOnly && (
        <div className="ga-stage-actions">
          <button
            className="ga-btn ga-btn--primary"
            disabled={!form.pi_name || !form.institution}
            onClick={() => {
            try { localStorage.removeItem(draftKey); } catch {}
            onAdvance(form);
          }}
          >
            {rewindMode ? 'Save & Re-run from here' : 'Continue'} <FiChevronRight />
          </button>
        </div>
      )}
    </div>
  );
}

const EMPTY_PARTNER_FORM = { work_stream: '', contact_name: '', institution: '', contact_title: '', fit_reason: '', prior_grants: '' };

function TeamBuildingStage({ proposal, onRun, onReset, onAdvance, readOnly }) {
  const [confirmations, setConfirmations] = useState({});
  const [customPartners, setCustomPartners] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_PARTNER_FORM);
  const [savedLoaded, setSavedLoaded] = useState(false);
  // team_suggestions may be a bare array (old proposals) or {suggestions, _raw_report}
  const tsData = proposal.team_suggestions;
  const suggestions = Array.isArray(tsData) ? tsData : (tsData?.suggestions || []);
  const rawTeamReport = Array.isArray(tsData) ? null : (tsData?._raw_report || null);

  useEffect(() => {
    // Only pre-populate saved partners on proposals that haven't had team building run yet
    const hasExistingPartners = suggestions.length > 0 ||
      (proposal.team_suggestions !== null && proposal.team_suggestions !== undefined);
    if (hasExistingPartners) return;
    fetch(getApiEndpoint('/api/grant-proposals/partner-profiles'), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(partners => {
        if (!partners.length) return;
        setCustomPartners(partners.map(p => ({ ...p, _fromLibrary: true })));
        setSavedLoaded(true);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (i, val) => setConfirmations(c => ({ ...c, [String(i)]: val }));

  const allDecided = (suggestions.length === 0 || suggestions.every((_, i) => String(i) in confirmations));

  const setField = (k, v) => setAddForm(f => ({ ...f, [k]: v }));

  const addPartner = () => {
    if (!addForm.contact_name || !addForm.institution) return;
    setCustomPartners(ps => [...ps, { ...addForm }]);
    setAddForm(EMPTY_PARTNER_FORM);
    setShowAddForm(false);
  };

  const removeCustom = (i) => setCustomPartners(ps => ps.filter((_, idx) => idx !== i));

  return (
    <AutoStageShell
      title="Team Building"
      description="The agent identifies capability gaps and recommends named partners for each work stream."
      status={proposal.stage_status}
      onRun={onRun}
      onReset={onReset}
      readOnly={readOnly}
    >
      {suggestions.length === 0 && rawTeamReport && (
        <div className="ga-team-raw-report">
          <p className="ga-team-raw-notice">
            The agent could not identify specific named contacts. Review the full scouting report below and add partners manually.
          </p>
          <pre className="ga-team-raw-pre">{rawTeamReport}</pre>
        </div>
      )}
      {(suggestions.length > 0 || customPartners.length > 0) && (
        <div className="ga-team-results">
          {suggestions.length > 0 && (
            <p className="ga-team-intro">
              Review each suggested partner. Confirm or decline — you can adjust before the draft is generated.
            </p>
          )}
          {suggestions.map((s, i) => (
            <div key={i} className={`ga-partner-card ${confirmations[i] === true ? 'ga-partner-card--confirmed' : confirmations[i] === false ? 'ga-partner-card--declined' : ''}`}>
              <div className="ga-partner-header">
                <div>
                  <span className="ga-work-stream">{s.work_stream}</span>
                  <h4>
                    {s.contact_name
                      ? <>{s.contact_name} <span className="ga-institution">@ {s.institution}</span></>
                      : <span className="ga-placeholder-warning">No named contact found — add manually</span>
                    }
                  </h4>
                  <span className="ga-contact-title">{s.contact_title}</span>
                </div>
                {!readOnly && (
                  <div className="ga-partner-actions">
                    <button
                      className={`ga-btn ga-btn--sm ${confirmations[i] === true ? 'ga-btn--confirmed' : 'ga-btn--outline'}`}
                      onClick={() => toggle(i, true)}
                    >
                      <FiCheck /> Include
                    </button>
                    <button
                      className={`ga-btn ga-btn--sm ${confirmations[i] === false ? 'ga-btn--declined' : 'ga-btn--outline'}`}
                      onClick={() => toggle(i, false)}
                    >
                      ✕ Decline
                    </button>
                  </div>
                )}
              </div>
              <p className="ga-fit-reason">{s.fit_reason}</p>
              <span className="ga-prior-grants">Prior grants: {s.prior_grants}</span>
            </div>
          ))}

          {/* User-added / saved partners */}
          {savedLoaded && (
            <p className="ga-profile-loaded-hint">
              <FiCheck /> {customPartners.length} partner{customPartners.length !== 1 ? 's' : ''} loaded from your partner library — review and adjust before continuing.
            </p>
          )}
          {customPartners.map((cp, i) => (
            <div key={`custom-${i}`} className="ga-partner-card ga-partner-card--confirmed ga-partner-card--custom">
              <div className="ga-partner-header">
                <div>
                  {cp.work_stream && <span className="ga-work-stream">{cp.work_stream}</span>}
                  <h4>{cp.contact_name} <span className="ga-institution">@ {cp.institution}</span></h4>
                  {cp.contact_title && <span className="ga-contact-title">{cp.contact_title}</span>}
                </div>
                {!readOnly && (
                  <div className="ga-partner-actions">
                    <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() => removeCustom(i)}>
                      <FiX /> Remove
                    </button>
                    {cp._fromLibrary && cp.id && (
                      <button
                        className="ga-btn ga-btn--sm ga-btn--outline ga-btn--danger"
                        title="Remove from your partner library"
                        onClick={() => {
                          fetch(getApiEndpoint(`/api/grant-proposals/partner-profiles/${cp.id}`), {
                            method: 'DELETE', headers: getAuthHeaders(),
                          }).catch(() => {});
                          removeCustom(i);
                        }}
                      >
                        <FiTrash2 /> Delete from library
                      </button>
                    )}
                  </div>
                )}
              </div>
              {cp.fit_reason && <p className="ga-fit-reason">{cp.fit_reason}</p>}
              {cp.prior_grants && <span className="ga-prior-grants">Prior grants: {cp.prior_grants}</span>}
              <span className="ga-custom-badge">{cp._fromLibrary ? 'From your library' : 'Added by you'}</span>
            </div>
          ))}
        </div>
      )}

      {/* Add Partner form */}
      {!readOnly && (
        <div className="ga-add-partner-section">
          {showAddForm ? (
            <div className="ga-add-partner-form">
              <h4 className="ga-add-partner-title">Add a Partner</h4>
              <div className="ga-form-row">
                <div className="ga-form-group">
                  <label>Name <span className="ga-label-required">*</span></label>
                  <input value={addForm.contact_name} onChange={e => setField('contact_name', e.target.value)} placeholder="Dr. Jane Smith" />
                </div>
                <div className="ga-form-group">
                  <label>Institution <span className="ga-label-required">*</span></label>
                  <input value={addForm.institution} onChange={e => setField('institution', e.target.value)} placeholder="MIT" />
                </div>
              </div>
              <div className="ga-form-row">
                <div className="ga-form-group">
                  <label>Title / Role</label>
                  <input value={addForm.contact_title} onChange={e => setField('contact_title', e.target.value)} placeholder="Associate Professor" />
                </div>
                <div className="ga-form-group">
                  <label>Work Stream</label>
                  <input value={addForm.work_stream} onChange={e => setField('work_stream', e.target.value)} placeholder="e.g. Data & Sensing" />
                </div>
              </div>
              <div className="ga-form-group">
                <label>Why this partner fits</label>
                <textarea rows={2} value={addForm.fit_reason} onChange={e => setField('fit_reason', e.target.value)} placeholder="Expertise in X, existing relationship with Y…" />
              </div>
              <div className="ga-form-group">
                <label>Prior relevant grants</label>
                <textarea rows={2} value={addForm.prior_grants} onChange={e => setField('prior_grants', e.target.value)} placeholder="NSF Award #..., $500K, 2022–2025" />
              </div>
              <div className="ga-add-partner-actions">
                <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={addPartner} disabled={!addForm.contact_name || !addForm.institution}>
                  <FiPlus /> Add Partner
                </button>
                <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => { setShowAddForm(false); setAddForm(EMPTY_PARTNER_FORM); }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button className="ga-btn ga-btn--outline" onClick={() => setShowAddForm(true)}>
              <FiPlus /> Add Your Own Partner
            </button>
          )}
        </div>
      )}

      {!readOnly && (
        <div className="ga-stage-actions">
          <button
            className="ga-btn ga-btn--primary"
            disabled={!allDecided && suggestions.length > 0}
            onClick={() => onAdvance({ confirmed: confirmations, custom_partners: customPartners })}
          >
            Confirm Team <FiChevronRight />
          </button>
          {!allDecided && suggestions.length > 0 && <span className="ga-hint">Review all suggested partners to continue</span>}
        </div>
      )}
    </AutoStageShell>
  );
}

// ── Diff helpers (GitHub-style unified diff) ──────────────────────────────────

function lcsOps(aArr, bArr) {
  const m = aArr.length, n = bArr.length;
  if (m * n > 120_000) {
    return [...aArr.map(s => ({ t: 'del', s })), ...bArr.map(s => ({ t: 'add', s }))];
  }
  const dp = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = aArr[i-1] === bArr[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);
  const ops = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aArr[i-1] === bArr[j-1]) { ops.unshift({ t: 'same', s: aArr[i-1] }); i--; j--; }
    else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) { ops.unshift({ t: 'add', s: bArr[j-1] }); j--; }
    else { ops.unshift({ t: 'del', s: aArr[i-1] }); i--; }
  }
  return ops;
}

function wordNodes(ops, side) {
  // side: 'del' → strip adds, highlight dels; 'add' → strip dels, highlight adds
  return ops
    .filter(o => o.t !== (side === 'del' ? 'add' : 'del'))
    .map((o, i) =>
      o.t === side
        ? <mark key={i} className={`ga-diff-word-${side}`}>{o.s}</mark>
        : <span key={i}>{o.s}</span>
    );
}

function UnifiedDiff({ original, revised }) {
  const lineOps = lcsOps(original.split('\n'), revised.split('\n'));

  // Group consecutive del/add runs so we can pair them for inline word highlights
  const rows = [];
  let k = 0;
  while (k < lineOps.length) {
    if (lineOps[k].t === 'same') {
      rows.push({ t: 'same', s: lineOps[k].s });
      k++;
    } else {
      const dels = [], adds = [];
      while (k < lineOps.length && lineOps[k].t === 'del') { dels.push(lineOps[k].s); k++; }
      while (k < lineOps.length && lineOps[k].t === 'add') { adds.push(lineOps[k].s); k++; }
      const pairs = Math.min(dels.length, adds.length);
      for (let p = 0; p < pairs; p++) {
        const wops = lcsOps(dels[p].split(/(\s+)/), adds[p].split(/(\s+)/));
        rows.push({ t: 'del', rich: wordNodes(wops, 'del') });
        rows.push({ t: 'add', rich: wordNodes(wops, 'add') });
      }
      for (let p = pairs; p < dels.length; p++) rows.push({ t: 'del', s: dels[p] });
      for (let p = pairs; p < adds.length; p++) rows.push({ t: 'add', s: adds[p] });
    }
  }

  return (
    <div className="ga-diff-unified">
      {rows.map((r, idx) => (
        <div key={idx} className={`ga-diff-line ga-diff-line--${r.t}`}>
          <span className="ga-diff-sign">{r.t === 'del' ? '-' : r.t === 'add' ? '+' : ' '}</span>
          <span className="ga-diff-content">{r.rich ?? r.s}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function DraftingStage({ proposal, onRun, onReset, onAdvance, readOnly }) {
  const sections = proposal.proposal_sections || {};
  const [edits, setEdits] = useState({});
  const [editing, setEditing] = useState(null);

  const sectionNames = Object.keys(sections);

  const downloadDraft = () => {
    const merged = { ...sections, ...edits };
    const text = Object.entries(merged)
      .map(([name, body]) => `## ${name}\n\n${body}`)
      .join('\n\n---\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${proposal.title.replace(/\s+/g, '_')}_draft.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AutoStageShell
      title="Proposal Draft"
      description="The agent drafts all proposal sections based on your NOFO, PI profile, and confirmed team."
      status={proposal.stage_status}
      onRun={onRun}
      onReset={onReset}
      readOnly={readOnly}
    >
      <div className="ga-draft-results">
        {sectionNames.length > 0 ? (
          sectionNames.map(name => (
            <div key={name} className="ga-section-card">
              <div className="ga-section-header">
                <h4>{name}</h4>
                <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() => setEditing(editing === name ? null : name)}>
                  {editing === name ? 'Done' : 'Edit'}
                </button>
              </div>
              {editing === name ? (
                <textarea
                  className="ga-section-editor"
                  rows={10}
                  value={edits[name] !== undefined ? edits[name] : sections[name]}
                  onChange={e => setEdits(d => ({ ...d, [name]: e.target.value }))}
                />
              ) : (
                <p className="ga-section-preview">
                  {(edits[name] !== undefined ? edits[name] : sections[name]).substring(0, 500)}
                  {(edits[name] !== undefined ? edits[name] : sections[name]).length > 500 ? '…' : ''}
                </p>
              )}
            </div>
          ))
        ) : (
          <p className="ga-hint">Draft complete — download to view the full document.</p>
        )}
        <div className="ga-stage-actions">
          <button className="ga-btn ga-btn--outline" onClick={downloadDraft}>
            <FiDownload /> Download Draft
          </button>
          {!readOnly && (
            <button
              className="ga-btn ga-btn--primary"
              onClick={() => onAdvance({ sections: edits })}
            >
              Continue to Review <FiChevronRight />
            </button>
          )}
        </div>
      </div>
    </AutoStageShell>
  );
}

function FeedbackButtons({ feedback, onFeedback }) {
  const verdict = feedback?.verdict || null;
  const [editingComment, setEditingComment] = useState(false);
  const [draft, setDraft] = useState(feedback?.comment || '');

  const handleVerdict = (v) => {
    if (v === verdict) {
      onFeedback({ verdict: '', comment: '' });
      setEditingComment(false);
    } else {
      if (v === 'disagree') setEditingComment(true);
      else setEditingComment(false);
      onFeedback({ verdict: v, comment: v === 'agree' ? '' : (feedback?.comment || '') });
    }
  };

  return (
    <div className="ga-feedback">
      <div className="ga-feedback-btns">
        <button
          className={`ga-feedback-btn ga-feedback-btn--agree${verdict === 'agree' ? ' ga-feedback-btn--active' : ''}`}
          onClick={() => handleVerdict('agree')}
          title="I'll address this"
        >✓ Agree</button>
        <button
          className={`ga-feedback-btn ga-feedback-btn--disagree${verdict === 'disagree' ? ' ga-feedback-btn--active' : ''}`}
          onClick={() => handleVerdict('disagree')}
          title="This doesn't apply to my proposal"
        >✗ Disagree</button>
      </div>
      {(editingComment || verdict === 'disagree') && (
        <textarea
          className="ga-feedback-comment"
          value={editingComment ? draft : (feedback?.comment || '')}
          onChange={e => setDraft(e.target.value)}
          onFocus={() => { setDraft(feedback?.comment || ''); setEditingComment(true); }}
          onBlur={() => {
            onFeedback({ verdict: 'disagree', comment: draft });
            setEditingComment(false);
          }}
          placeholder="Why do you disagree? (optional)"
          rows={2}
        />
      )}
    </div>
  );
}


function ComplianceStage({ proposal, onRun, onReset, onAdvance, readOnly }) {
  const report = proposal.compliance_report;
  const parsed = parseComplianceReport(report);

  const statusClass = parsed?.overall
    ? parsed.overall.includes('READY') ? 'ready'
    : parsed.overall.includes('CRITICAL') ? 'critical' : 'needs-work'
    : '';

  const downloadDraft = () => {
    const sections = proposal.proposal_sections || {};
    const text = Object.entries(sections)
      .map(([name, body]) => `## ${name}\n\n${body}`)
      .join('\n\n---\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${proposal.title.replace(/\s+/g, '_')}_draft.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AutoStageShell
      title="Panel Compliance Check"
      description="Six compliance specialists audit every NOFO requirement in parallel and flag gaps before submission."
      status={proposal.stage_status}
      onRun={onRun}
      onReset={onReset}
      readOnly={readOnly}
    >
      {parsed && (
        <div className="ga-compliance-results">
          {/* Status banner */}
          <div className={`ga-compliance-status ga-compliance-status--${statusClass}`}>
            <strong>{parsed.overall || 'Review complete'}</strong>
            {parsed.score && <div className="ga-compliance-score">{parsed.score}</div>}
          </div>
          {parsed.summary && <p className="ga-compliance-summary">{parsed.summary}</p>}

          {/* Quick counts */}
          <div className="ga-compliance-quick-counts">
            {parsed.failed.length > 0 && (
              <span className="ga-compliance-count-badge ga-compliance-count-badge--fail">
                ✗ {parsed.failed.length} must fix
              </span>
            )}
            {parsed.review.length > 0 && (
              <span className="ga-compliance-count-badge ga-compliance-count-badge--warn">
                ⚠ {parsed.review.length} needs review
              </span>
            )}
            {parsed.passed.length > 0 && (
              <span className="ga-compliance-count-badge ga-compliance-count-badge--pass">
                ✓ {parsed.passed[0]?.text || 'requirements satisfied'}
              </span>
            )}
          </div>

          <div className="ga-stage-actions">
            <button className="ga-btn ga-btn--outline" onClick={downloadDraft}>
              <FiDownload /> Download Draft
            </button>
            <button className="ga-btn ga-btn--outline" onClick={() => {
              const md = formatComplianceAsMarkdown(parsed, proposal.title) || report;
              const blob = new Blob([md], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `${proposal.title.replace(/\s+/g, '_')}_compliance.md`; a.click();
              URL.revokeObjectURL(url);
            }}>
              <FiDownload /> Export Report
            </button>
            {!readOnly && (
              <button className="ga-btn ga-btn--primary" onClick={() => onAdvance({})}>
                Mark Complete <FiChevronRight />
              </button>
            )}
          </div>
        </div>
      )}
    </AutoStageShell>
  );
}

// ── DOCX helpers ──────────────────────────────────────────────────────────────

function formatComplianceAsMarkdown(compliance, title) {
  if (!compliance) return '';
  const lines = [];

  if (title) lines.push(`# ${title} — Compliance Report\n`);

  lines.push(`## Overall Status: ${compliance.overall || 'Review Complete'}`);
  if (compliance.score) lines.push(`**Score:** ${compliance.score}`);
  if (compliance.summary) lines.push(`\n${compliance.summary}`);
  lines.push('');

  if (compliance.failed.length > 0) {
    lines.push(`## ✗ Must Fix (${compliance.failed.length})`);
    for (const item of compliance.failed) {
      lines.push(`\n### ${item.text}`);
      if (item.issue) lines.push(`**Issue:** ${item.issue}`);
      if (item.fix)   lines.push(`**Fix:** ${item.fix}`);
    }
    lines.push('');
  }

  if (compliance.review.length > 0) {
    lines.push(`## ⚠ Needs Review (${compliance.review.length})`);
    for (const item of compliance.review) {
      lines.push(`\n### ${item.text || item}`);
      if (item.issue) lines.push(`**Issue:** ${item.issue}`);
      if (item.fix)   lines.push(`**Action:** ${item.fix}`);
    }
    lines.push('');
  }

  if (compliance.passed.length > 0) {
    lines.push(`## ✓ Satisfied (${compliance.passed.length})`);
    for (const item of compliance.passed) {
      lines.push(`- **${item.text || item}**${item.evidence ? `  \n  ${item.evidence}` : ''}`);
    }
    lines.push('');
  }

  if (compliance.placeholders.length > 0) {
    lines.push('## Remaining Placeholders');
    for (const p of compliance.placeholders) lines.push(`- ${p}`);
    lines.push('');
  }

  if (compliance.nextSteps) {
    lines.push('## Recommended Next Steps');
    const steps = compliance.nextSteps.split('\n').filter(Boolean);
    steps.forEach((s, i) => lines.push(`${i + 1}. ${s.replace(/^\d+\.\s*/, '')}`));
    lines.push('');
  }

  if (compliance.finalNote) {
    lines.push('---');
    lines.push(`*${compliance.finalNote}*`);
  }

  return lines.join('\n');
}

async function downloadAsDocx(markdownText, filename) {
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } = await import('docx');
  const lines = markdownText.split('\n');
  const children = [];

  for (const line of lines) {
    if (line.startsWith('## ')) {
      children.push(new Paragraph({ text: line.slice(3), heading: HeadingLevel.HEADING_2 }));
    } else if (line.startsWith('# ')) {
      children.push(new Paragraph({ text: line.slice(2), heading: HeadingLevel.HEADING_1 }));
    } else if (line.startsWith('### ')) {
      children.push(new Paragraph({ text: line.slice(4), heading: HeadingLevel.HEADING_3 }));
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      children.push(new Paragraph({ text: line.slice(2), bullet: { level: 0 } }));
    } else if (line.trim() === '---') {
      children.push(new Paragraph({ text: '', border: { bottom: { style: 'single', size: 6 } } }));
    } else {
      children.push(new Paragraph({ children: [new TextRun(line)] }));
    }
  }

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Compliance report parser ───────────────────────────────────────────────────

function parseComplianceReport(text) {
  if (!text) return null;

  // Try JSON format first (structured output from agent)
  try {
    const raw = text.trim();
    const jsonStart = raw.indexOf('{');
    if (jsonStart !== -1) {
      const data = JSON.parse(raw.slice(jsonStart));

      // ── Panel compliance format (has `reviewers` + `synthesis`) ──
      if (data.reviewers && data.synthesis) {
        const s = data.synthesis;
        const placeholderReviewer = data.reviewers.find(r => r.id === 'placeholder_hunter');
        const placeholders = placeholderReviewer
          ? [
              ...(placeholderReviewer.failed   || []).map(f => f.text || ''),
              ...(placeholderReviewer.warnings || []).map(w => w.text || ''),
            ].filter(Boolean)
          : [];
        return {
          overall:      s.overall || '',
          score:        s.score   || '',
          summary:      s.summary || '',
          failed: (s.critical_blockers || []).map(b => ({
            text:  b.text  || '',
            issue: b.reviewer ? `Flagged by: ${b.reviewer}` : '',
            fix:   b.fix   || '',
          })),
          review: (s.warnings || []).map(w => ({
            text:  w.text  || '',
            issue: w.reviewer ? `Flagged by: ${w.reviewer}` : '',
            fix:   w.fix   || '',
          })),
          passed: s.passed_count > 0
            ? [{ text: `${s.passed_count} requirements satisfied across all specialists` }]
            : [],
          placeholders,
          nextSteps:  '',
          finalNote:  '',
          // Panel-specific extras used by the per-reviewer accordion
          reviewers:  data.reviewers,
          isPanel:    true,
          grantType:  data.grant_type || '',
          runAt:      data.run_at     || '',
        };
      }

      const scoreObj = data.score || {};
      const scoreStr = scoreObj.satisfied != null && scoreObj.total != null
        ? `${scoreObj.satisfied} / ${scoreObj.total} requirements satisfied${scoreObj.note ? ` — ${scoreObj.note}` : ''}`
        : '';

      const passed = (data.passed || []).map(p =>
        typeof p === 'string' ? { text: p, fix: '' }
        : { text: p.requirement || p.text || JSON.stringify(p), evidence: p.evidence || '', fix: '' }
      );

      const failed = (data.failed || []).map(f =>
        typeof f === 'string' ? { text: f, fix: '' }
        : { text: f.requirement || f.text || JSON.stringify(f), fix: f.fix || f.action || '', issue: f.issue || '' }
      );

      const review = (data.needs_review || data.review || []).map(r =>
        typeof r === 'string' ? { text: r, fix: '' }
        : { text: r.item || r.text || JSON.stringify(r), fix: r.action || r.fix || '', issue: r.issue || '' }
      );

      const placeholders = (data.placeholders || []).map(p =>
        typeof p === 'string' ? p
        : `${p.placeholder || p.text || ''}${p.location ? ` [${p.location}]` : ''}${p.needed ? ` — ${p.needed}` : ''}`
      );

      const nextSteps = (data.recommended_next_steps || data.next_steps || []).map(s =>
        typeof s === 'string' ? s
        : `${s.priority != null ? `${s.priority}. ` : ''}${s.action || JSON.stringify(s)}`
      );

      return {
        overall: data.overall_status || data.overall || '',
        score: scoreStr,
        summary: data.summary || '',
        passed,
        failed,
        review,
        placeholders,
        nextSteps: nextSteps.join('\n'),
        finalNote: data.final_note || '',
      };
    }
  } catch (_) { /* fall through to markdown parser */ }

  // Markdown fallback
  const result = { overall: '', score: '', summary: '', passed: [], failed: [], review: [], placeholders: [], nextSteps: '', finalNote: '' };
  let section = null;

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (/^#{1,3}\s*Overall Status/i.test(trimmed))          { section = 'overall'; continue; }
    if (/^#{1,3}\s*[✓✔].*(Pass|Met)/i.test(trimmed))       { section = 'passed';  continue; }
    if (/^#{1,3}\s*[✗✘✕].*(Fail|Fix|Must)/i.test(trimmed)) { section = 'failed';  continue; }
    if (/^#{1,3}\s*[⚠⚡?].*(Review|Warn)/i.test(trimmed))  { section = 'review';  continue; }
    if (/^#{1,3}\s*(Remaining|Placeholder)/i.test(trimmed)) { section = 'placeholders'; continue; }
    if (/^#{1,3}\s*(Next Step|Recommend)/i.test(trimmed))   { section = 'nextSteps'; continue; }
    if (/^#{1,3}/.test(trimmed)) { section = null; continue; }

    if (!trimmed) continue;

    if (section === 'overall') {
      if (/^score:/i.test(trimmed)) result.score = trimmed;
      else result.overall += (result.overall ? ' ' : '') + trimmed;
    } else if (section === 'passed') {
      if (trimmed.startsWith('-')) result.passed.push({ text: trimmed.slice(1).trim(), fix: '' });
    } else if (section === 'failed') {
      if (trimmed.startsWith('-')) result.failed.push({ text: trimmed.slice(1).trim(), fix: '', issue: '' });
      else if (/^fix:/i.test(trimmed) && result.failed.length)
        result.failed[result.failed.length - 1].fix = trimmed.replace(/^fix:\s*/i, '');
    } else if (section === 'review') {
      if (trimmed.startsWith('-')) result.review.push({ text: trimmed.slice(1).trim(), fix: '', issue: '' });
    } else if (section === 'placeholders') {
      if (trimmed.startsWith('-')) result.placeholders.push(trimmed.slice(1).trim());
    } else if (section === 'nextSteps') {
      result.nextSteps += (result.nextSteps ? '\n' : '') + trimmed;
    }
  }

  return result;
}

// ── Calibrated Review report formatter ────────────────────────────────────────

function formatPanelReviewReport(pr, feedbackState, title) {
  if (!pr) return '';
  const lines = [];
  const s = pr.synthesis || {};
  const reviewers = pr.reviewers || [];

  const getFb = (reviewerId, itemType, idx) => {
    const key = `${reviewerId}::${itemType}::${idx}`;
    if (feedbackState[key] !== undefined) return feedbackState[key];
    if (reviewerId === '__synthesis__') {
      if (itemType === 'top_strengths') return pr.synthesis?.top_strengths_feedback?.[idx] || null;
      return pr.synthesis?.[itemType]?.[idx]?.feedback || null;
    }
    const rv = reviewers.find(r => r.id === reviewerId);
    return (rv?.[itemType] || [])[idx]?.feedback || null;
  };

  const fbLabel = fb => {
    if (!fb?.verdict) return '';
    return fb.verdict === 'agree'
      ? '  ✓ *You agreed*'
      : `  ✗ *You disagreed${fb.comment ? `: "${fb.comment}"` : ''}*`;
  };

  lines.push(`# ${title || 'Grant Proposal'} — Calibrated Review Report\n`);
  lines.push(`**Grant Type:** ${pr.grant_type || 'Auto-detected'}`);
  lines.push(`**Run:** ${pr.run_at ? new Date(pr.run_at).toLocaleString() : 'Unknown'}`);
  lines.push(`**Overall:** ${s.overall_readiness || '—'}\n`);
  lines.push('---\n');

  // Top strengths
  if ((s.top_strengths || []).length > 0) {
    lines.push('## Panel Strengths\n');
    s.top_strengths.forEach((strength, i) => {
      const fb = getFb('__synthesis__', 'top_strengths', i);
      lines.push(`- ${strength}${fbLabel(fb)}`);
    });
    lines.push('');
  }

  // Priority issues
  if ((s.ranked_issues || []).length > 0) {
    lines.push('## Priority Issues\n');
    s.ranked_issues.forEach((issue, i) => {
      const fb = getFb('__synthesis__', 'ranked_issues', i);
      lines.push(`### ${i + 1}. [${(issue.severity || '').toUpperCase()}] ${issue.section || ''}`);
      lines.push(issue.issue || '');
      if (issue.fix) lines.push(`**Fix:** ${issue.fix}`);
      if (issue.flagged_by?.length) lines.push(`**Flagged by:** ${issue.flagged_by.map(id => id.replace(/_/g, ' ')).join(', ')}`);
      if (fb?.verdict) lines.push(fbLabel(fb).trim());
      lines.push('');
    });
  }

  // Per-reviewer
  if (reviewers.length > 0) {
    lines.push('---\n');
    lines.push('## Individual Reviewer Notes\n');
    reviewers.forEach(reviewer => {
      lines.push(`### ${reviewer.label}`);
      if (reviewer.overall_comment) lines.push(`> ${reviewer.overall_comment}\n`);
      if ((reviewer.strengths || []).length > 0) {
        lines.push('**Strengths:**');
        reviewer.strengths.forEach(s => lines.push(`- ${s}`));
        lines.push('');
      }
      if ((reviewer.issues || []).length > 0) {
        lines.push('**Issues:**');
        reviewer.issues.forEach((issue, i) => {
          const fb = getFb(reviewer.id, 'issues', i);
          lines.push(`- **[${(issue.severity || '').toUpperCase()}] ${issue.section || ''}** — ${issue.issue}`);
          if (issue.fix) lines.push(`  Fix: ${issue.fix}`);
          if (fb?.verdict) lines.push(`  ${fbLabel(fb).trim()}`);
        });
        lines.push('');
      }
    });
  }

  lines.push('---');
  lines.push('*Generated by Mnevia Calibrated Review — not a score prediction.*');
  return lines.join('\n');
}


// ── DoneStage ─────────────────────────────────────────────────────────────────

// Colours grouped by semantic role — same hue family for same type of concern
const REVIEWER_COLORS = {
  // Blue — significance / intellectual merit / scientific quality
  significance:       { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  intellectual_merit: { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  scientific_merit:   { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  research_plan:      { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  // Indigo — innovation / IP
  innovation:         { bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3', dot: '#6366f1' },
  innovation_ip:      { bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3', dot: '#6366f1' },
  // Purple — approach / methods
  approach:           { bg: '#f5f3ff', border: '#ddd6fe', text: '#6d28d9', dot: '#8b5cf6' },
  approach_methods:   { bg: '#f5f3ff', border: '#ddd6fe', text: '#6d28d9', dot: '#8b5cf6' },
  // Teal — investigator / team / feasibility / candidate
  investigator_env:   { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  feasibility:        { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  technical_feasibility: { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  team_execution:     { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  candidate:          { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  mentor_environment: { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  training_activities:{ bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  // Green — program officer / agency alignment / commercial potential
  program_officer:    { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', dot: '#10b981' },
  agency_alignment:   { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', dot: '#10b981' },
  commercial_potential:{ bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', dot: '#10b981' },
  // Orange — broader impact / equity
  broader_impacts:    { bg: '#fffbeb', border: '#fde68a', text: '#92400e', dot: '#f59e0b' },
  broader_impact:     { bg: '#fffbeb', border: '#fde68a', text: '#92400e', dot: '#f59e0b' },
  equity_reviewer:    { bg: '#fffbeb', border: '#fde68a', text: '#92400e', dot: '#f59e0b' },
  // Red — skeptic
  skeptic:            { bg: '#fff1f2', border: '#fecdd3', text: '#9f1239', dot: '#ef4444' },
  // Legacy (backwards compat with existing stored reviews)
  domain_expert:      { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  biostatistician:    { bg: '#f5f3ff', border: '#ddd6fe', text: '#6d28d9', dot: '#8b5cf6' },
  budget_analyst:     { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
};

const SEVERITY_META = {
  high:   { label: 'High',   cls: 'ga-severity--high'   },
  medium: { label: 'Medium', cls: 'ga-severity--medium' },
  low:    { label: 'Low',    cls: 'ga-severity--low'    },
};

export function DoneStage({ proposal, onRefresh, user = null, onAdvance = null, defaultTab = 'draft', readOnly = false, tab: tabProp, onTabChange }) {
  const [internalTab, setInternalTab] = useState(defaultTab);
  const tab    = tabProp ?? internalTab;
  const setTab = onTabChange ?? setInternalTab;
  const [editingSection, setEditing] = useState(null);
  const editsDraftKey = `ga_draft_edits_${proposal.id}`;
  const [edits, setEdits] = useState(() => {
    try { return JSON.parse(localStorage.getItem(editsDraftKey) || '{}'); } catch { return {}; }
  });
  const [expandedSections, setExpanded] = useState({});
  const [saving, setSaving]         = useState(false);
  const [rerunning, setRerunning]   = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [runningPanel, setRunningPanel] = useState(false);
  const [panelGrantType, setPanelGrantType] = useState('');
  const [complianceGrantType, setComplianceGrantType] = useState('');
  const [expandedComplianceReviewers, setExpandedComplianceReviewers] = useState({});
  const [reviewFeedback, setReviewFeedback] = useState({});
  const [complianceFeedback, setComplianceFeedback] = useState({});
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted]   = useState(false);
  const [iterating, setIterating]       = useState(false);
  const [revisions, setRevisions]       = useState(null);
  const [revisionDrafts, setRevisionDrafts] = useState({});
  const [revisionStatus, setRevisionStatus] = useState({});
  const [collapsedRevisions, setCollapsedRevisions] = useState({});
  const [savedAt, setSavedAt]       = useState(null);
  const [expandedReviewers, setExpandedReviewers] = useState({});
  const uploadRef = React.useRef(null);

  // Outcome tracking
  const [outcome, setOutcome]           = useState(proposal.funding_outcome || '');
  const [outcomeScore, setOutcomeScore] = useState(proposal.priority_score ?? '');
  const [outcomeSaving, setOutcomeSaving] = useState(false);
  const [outcomeSaved, setOutcomeSaved]   = useState(false);

  // Reference-corpus contribution (§1.6 of RAG plan)
  const [contributing, setContributing] = useState(!!proposal.contributed_to_corpus);
  const [consentStatus, setConsentStatus] = useState(user?.data_consent_status || 'none');
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [contribSaving, setContribSaving] = useState(false);

  // Reviewer feedback
  const [reviewerFeedbackText, setReviewerFeedbackText] = useState(proposal.reviewer_feedback_raw || '');
  const [feedbackSaving, setFeedbackSaving]   = useState(false);
  const [feedbackSavedMsg, setFeedbackSavedMsg] = useState('');
  const [critiques, setCritiques] = useState(null);

  // ── Team collaboration state ──────────────────────────────────────────────
  const [teamMembers, setTeamMembers] = useState([]);
  const [sectionAssignments, setSectionAssignments] = useState(proposal.section_assignments || {});
  const [assigningSection, setAssigningSection] = useState(null);  // section name or null
  const [showRequestReview, setShowRequestReview] = useState(false);

  useEffect(() => {
    fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/team-members`), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setTeamMembers)
      .catch(() => setTeamMembers([]));
  }, [proposal.id]);

  useEffect(() => {
    setSectionAssignments(proposal.section_assignments || {});
  }, [proposal.section_assignments]);

  // List of teams the user can attach this proposal to (shown only when the
  // proposal has no collection — otherwise the collection's team is the
  // source of truth and can't be overridden from here).
  const [userTeams, setUserTeams] = useState([]);
  const [showTeamPicker, setShowTeamPicker] = useState(false);
  const canPickTeam = !proposal.collection_id && proposal.user_id === user?.id;
  useEffect(() => {
    if (!canPickTeam) return;
    fetch(getApiEndpoint('/api/teams'), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setUserTeams)
      .catch(() => setUserTeams([]));
  }, [canPickTeam]);

  const attachTeam = async (teamId) => {
    setShowTeamPicker(false);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/team`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_id: teamId }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to attach team');
      if (onRefresh) onRefresh(proposal.id);
    } catch (e) { alert(e.message); }
  };
  const currentTeamName = () => {
    if (proposal.team_id) {
      const t = userTeams.find(x => x.id === proposal.team_id);
      return t ? t.name : null;
    }
    return null;
  };

  const assignSection = async (sectionName, userId) => {
    const next = { ...sectionAssignments };
    if (userId) next[sectionName] = userId;
    else delete next[sectionName];
    setSectionAssignments(next);
    setAssigningSection(null);
    try {
      await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/section-assignments`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments: next }),
      });
      if (onRefresh) onRefresh(proposal.id);
    } catch (_) { /* non-fatal */ }
  };

  const requestReview = async (reviewerUserId) => {
    try {
      await fetch(getApiEndpoint('/api/grant-proposals/peer-reviews'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewer_user_id: reviewerUserId, proposal_id: proposal.id }),
      });
      setShowRequestReview(false);
      alert('Review requested!');
    } catch (_) {}
  };

  const teamMemberName = (uid) => {
    const m = teamMembers.find(x => x.user_id === uid);
    return m ? (m.display_name || m.username) : `User ${uid}`;
  };

  useEffect(() => {
    try { localStorage.setItem(editsDraftKey, JSON.stringify(edits)); } catch {}
  }, [edits]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasUnsaved = Object.keys(edits).length > 0;

  const saveSections = async () => {
    setSaving(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/sections`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections: edits }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      setEdits({});
      try { localStorage.removeItem(editsDraftKey); } catch {}
      setSavedAt(new Date());
      if (onRefresh) onRefresh(proposal.id);
      return true;
    } catch (e) {
      alert(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const rerunCompliance = async () => {
    setRerunning(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/rerun-compliance`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ grant_type: complianceGrantType }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Re-run failed');
      if (onRefresh) onRefresh(proposal.id);
      setExpandedComplianceReviewers({});
      setComplianceFeedback({});
    } catch (e) {
      alert(e.message);
    } finally {
      setRerunning(false);
    }
  };

  const runPanelReview = async () => {
    setRunningPanel(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/panel-review`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ grant_type: panelGrantType }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Panel review failed');
      if (onRefresh) onRefresh(proposal.id);
      setExpandedReviewers({});
      setReviewFeedback({});
      setRevisions(null);
      setRevisionDrafts({});
      setRevisionStatus({});
    } catch (e) {
      alert(e.message);
    } finally {
      setRunningPanel(false);
    }
  };

  // ── Feedback helpers ─────────────────────────────────────────────────────
  const getReviewFeedback = (reviewerId, itemType, itemIndex) => {
    const key = `${reviewerId}::${itemType}::${itemIndex}`;
    if (reviewFeedback.hasOwnProperty(key)) return reviewFeedback[key];
    const pr = proposal.panel_review;
    if (!pr) return null;
    if (reviewerId === '__synthesis__') {
      if (itemType === 'top_strengths')
        return pr.synthesis?.top_strengths_feedback?.[itemIndex] || null;
      return pr.synthesis?.[itemType]?.[itemIndex]?.feedback || null;
    }
    const rv = (pr.reviewers || []).find(r => r.id === reviewerId);
    return (rv?.[itemType] || [])[itemIndex]?.feedback || null;
  };

  const saveReviewFeedback = (reviewerId, itemType, itemIndex, fb) => {
    const key = `${reviewerId}::${itemType}::${itemIndex}`;
    setReviewFeedback(prev => ({ ...prev, [key]: fb }));
    fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/panel-review/feedback`), {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewer_id: reviewerId, item_type: itemType, item_index: itemIndex, verdict: fb.verdict || '', comment: fb.comment || '' }),
    }).catch(console.error);
  };

  const submitAllFeedback = async () => {
    const entries = Object.entries(reviewFeedback);
    if (entries.length === 0) { setFeedbackSubmitted(true); setTimeout(() => setFeedbackSubmitted(false), 2500); return; }
    setFeedbackSubmitting(true);
    try {
      await Promise.all(entries.map(([key, fb]) => {
        const [reviewerId, itemType, itemIndex] = key.split('::');
        return fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/panel-review/feedback`), {
          method: 'PUT',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviewer_id: reviewerId, item_type: itemType, item_index: parseInt(itemIndex, 10), verdict: fb?.verdict || '', comment: fb?.comment || '' }),
        });
      }));
      setFeedbackSubmitted(true);
      setTimeout(() => setFeedbackSubmitted(false), 2500);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const runIteration = async () => {
    setIterating(true);
    setRevisions(null);
    setRevisionDrafts({});
    setRevisionStatus({});
    setCollapsedRevisions({});
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/iterate`), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Iteration failed'); }
      const data = await res.json();
      const revs = data.revisions || {};
      setRevisions(revs);
      // Pre-fill drafts with the LLM output
      const drafts = {};
      Object.entries(revs).forEach(([sec, v]) => { drafts[sec] = v.revised; });
      setRevisionDrafts(drafts);
      // Mirror the paper pipeline: jump to the Draft step so the user sees the diffs
      // against the original right where they will accept/edit them.
      setTab('draft');
    } catch (err) {
      alert(err.message);
    } finally {
      setIterating(false);
    }
  };

  const getComplianceFeedback = (reviewerId, itemType, itemIndex) => {
    const key = `${reviewerId}::${itemType}::${itemIndex}`;
    if (complianceFeedback.hasOwnProperty(key)) return complianceFeedback[key];
    try {
      const raw = JSON.parse(proposal.compliance_report || '{}');
      const rv = (raw.reviewers || []).find(r => r.id === reviewerId);
      return (rv?.[itemType] || [])[itemIndex]?.feedback || null;
    } catch (_) { return null; }
  };

  const saveComplianceFeedback = (reviewerId, itemType, itemIndex, fb) => {
    const key = `${reviewerId}::${itemType}::${itemIndex}`;
    setComplianceFeedback(prev => ({ ...prev, [key]: fb }));
    fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/compliance/feedback`), {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewer_id: reviewerId, item_type: itemType, item_index: itemIndex, verdict: fb.verdict || '', comment: fb.comment || '' }),
    }).catch(console.error);
  };


  const toggleCorpusContribution = async (nextValue) => {
    if (nextValue && consentStatus !== 'granted') {
      setShowConsentModal(true);
      return;
    }
    setContribSaving(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/contribute-to-corpus`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ contribute: nextValue }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update corpus contribution');
      setContributing(nextValue);
      if (onRefresh) onRefresh(proposal.id);
    } catch (e) {
      alert(e.message);
    } finally {
      setContribSaving(false);
    }
  };

  const saveOutcome = async (newOutcome, newScore) => {
    setOutcomeSaving(true);
    setOutcomeSaved(false);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/outcome`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          funding_outcome: newOutcome,
          priority_score: newScore !== '' ? parseInt(newScore, 10) : null,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      setOutcomeSaved(true);
      if (onRefresh) onRefresh(proposal.id);
    } catch (e) {
      alert(e.message);
    } finally {
      setOutcomeSaving(false);
    }
  };

  const saveReviewerFeedback = async () => {
    setFeedbackSaving(true);
    setFeedbackSavedMsg('');
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/reviewer-feedback`), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewer_feedback_raw: reviewerFeedbackText }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      setFeedbackSavedMsg('Saved — analyzing patterns in background…');
      if (onRefresh) onRefresh(proposal.id);
    } catch (e) {
      alert(e.message);
    } finally {
      setFeedbackSaving(false);
    }
  };

  const loadCritiques = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/grant-proposals/pi-profile'), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.recurring_reviewer_critiques?.length) setCritiques(data.recurring_reviewer_critiques);
    } catch (_) {}
  };

  const uploadDraft = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${proposal.id}/upload-draft`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: form,
      });
      if (!res.ok) {
        let detail = 'Upload failed';
        try { detail = (await res.json()).detail || detail; } catch { detail = (await res.text()) || detail; }
        throw new Error(detail);
      }
      setEdits({});
      setSavedAt(new Date());
      if (onRefresh) onRefresh(proposal.id);
    } catch (e) {
      alert(e.message);
    } finally {
      setUploading(false);
    }
  };

  const sections  = proposal.proposal_sections || {};
  const draftText = Object.entries({ ...sections, ...edits })
    .map(([name, body]) => `## ${name}\n\n${body}`)
    .join('\n\n---\n\n');

  const compliance = parseComplianceReport(proposal.compliance_report);

  const statusClass = compliance?.overall
    ? compliance.overall.includes('READY') ? 'ready'
    : compliance.overall.includes('CRITICAL') ? 'critical' : 'needs-work'
    : '';

  const complianceMd = formatComplianceAsMarkdown(compliance, proposal.title) || proposal.compliance_report || '';

  const OUTCOME_CONFIG = {
    funded:    { label: 'Funded',    className: 'ga-outcome--funded' },
    rejected:  { label: 'Not funded', className: 'ga-outcome--rejected' },
    pending:   { label: 'Pending',   className: 'ga-outcome--pending' },
    withdrawn: { label: 'Withdrawn', className: 'ga-outcome--withdrawn' },
  };

  // Revisions panel — rendered on the Draft tab so accept/decline happens right
  // where the running draft text lives. Matches the paper pipeline's layout.
  const renderRevisionsPanel = () => {
    if (iterating) {
      return (
        <div className="ga-revision-loading">
          <FiLoader className="ga-spin" />
          <span>Rewriting sections to address reviewer issues…</span>
        </div>
      );
    }
    if (revisions && Object.keys(revisions).length === 0) {
      return (
        <div className="ga-revision-empty">
          No sections could be matched to the reviewer issues. Try running the panel review again or editing sections manually.
        </div>
      );
    }
    if (!revisions || Object.keys(revisions).length === 0) return null;

    return (
      <div className="ga-revision-panel">
        <h4 className="ga-panel-section-title ga-panel-section-title--revisions">
          Proposed Revisions ({Object.keys(revisions).length} section{Object.keys(revisions).length !== 1 ? 's' : ''})
        </h4>
        {!isGuideSeen(user?.id, 'iteration_guide') && (
          <div className="ga-iteration-guide">
            <strong>How to iterate your proposal:</strong>
            <div className="ga-iteration-steps">
              <span>&#9312; Review each issue — Accept or Skip the fix</span>
              <span>&#9313; Accepted fixes appear as unsaved edits in the Draft tab</span>
              <span>&#9314; Click "Save" in the Draft tab, then re-run Panel Review</span>
              <span>&#9315; Repeat until no high-severity issues remain</span>
            </div>
            <button onClick={(e) => { e.target.closest('.ga-iteration-guide').remove(); markGuideSeen(user?.id, 'iteration_guide'); }}>Got it</button>
          </div>
        )}
        <p className="ga-revision-instructions">
          Edit each revision as needed, then Accept to stage it for saving — or Skip to leave the section unchanged. Accepted revisions appear as unsaved changes above; click <strong>Save</strong> at the top to commit, then re-run Panel Review.
        </p>
        {Object.entries(revisions).map(([sectionName, rev]) => {
          const status = revisionStatus[sectionName];
          const draft  = revisionDrafts[sectionName] ?? rev.revised;
          const isDecided = ['kept-original', 'kept-revised', 'saved-edit'].includes(status);
          const isEditing = status === 'editing';
          const collapsed = !!collapsedRevisions[sectionName];
          const toggleCollapse = () => setCollapsedRevisions(prev => ({ ...prev, [sectionName]: !collapsed }));
          return (
            <div
              key={sectionName}
              className={`ga-revision-card${status === 'kept-original' ? ' ga-revision-card--skipped' : (status === 'kept-revised' || status === 'saved-edit') ? ' ga-revision-card--accepted' : ''}`}
            >
              <div className="ga-revision-card-header" onClick={toggleCollapse} style={{ cursor: 'pointer' }}>
                <span className="ga-revision-chevron">{collapsed ? <FiChevronRight /> : <FiChevronDown />}</span>
                <span className="ga-revision-section-name">{sectionName}</span>
                <div className="ga-revision-actions" onClick={e => e.stopPropagation()}>
                  {status === 'kept-original' ? (<>
                    <span className="ga-revision-badge ga-revision-badge--skipped">Original kept</span>
                    <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={() => setRevisionStatus(prev => ({ ...prev, [sectionName]: undefined }))}>Undo</button>
                  </>) : status === 'kept-revised' ? (<>
                    <span className="ga-revision-badge ga-revision-badge--accepted"><FiCheck /> Revised accepted</span>
                    <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={() => setRevisionStatus(prev => ({ ...prev, [sectionName]: undefined }))}>Undo</button>
                  </>) : status === 'saved-edit' ? (<>
                    <span className="ga-revision-badge ga-revision-badge--accepted"><FiCheck /> Custom edit saved</span>
                    <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={() => setRevisionStatus(prev => ({ ...prev, [sectionName]: 'editing' }))}>Re-edit</button>
                  </>) : isEditing ? (<>
                    <span className="ga-revision-edit-hint">
                      Start from:
                      <button className="ga-btn-link" onClick={() => setRevisionDrafts(prev => ({ ...prev, [sectionName]: rev.original }))}>original</button>
                      ·
                      <button className="ga-btn-link" onClick={() => setRevisionDrafts(prev => ({ ...prev, [sectionName]: rev.revised }))}>revised</button>
                    </span>
                    <button className="ga-btn ga-btn--sm ga-btn--confirmed" onClick={() => {
                      setEdits(prev => ({ ...prev, [sectionName]: draft }));
                      setRevisionStatus(prev => ({ ...prev, [sectionName]: 'saved-edit' }));
                    }}><FiCheck /> Save edit</button>
                    <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() => {
                      setRevisionDrafts(prev => ({ ...prev, [sectionName]: rev.revised }));
                      setRevisionStatus(prev => ({ ...prev, [sectionName]: undefined }));
                    }}>Cancel</button>
                  </>) : (<>
                    <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() => setRevisionStatus(prev => ({ ...prev, [sectionName]: 'kept-original' }))}>
                      Keep original
                    </button>
                    <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() => setRevisionStatus(prev => ({ ...prev, [sectionName]: 'editing' }))}>
                      <FiEdit2 /> Edit
                    </button>
                    <button className="ga-btn ga-btn--sm ga-btn--confirmed" onClick={() => {
                      setEdits(prev => ({ ...prev, [sectionName]: rev.revised }));
                      setRevisionStatus(prev => ({ ...prev, [sectionName]: 'kept-revised' }));
                    }}><FiCheck /> Keep revised</button>
                  </>)}
                </div>
              </div>

              {!collapsed && (<>
                <div className="ga-revision-issues">
                  {rev.issues_addressed.map((issue, i) => (
                    <span key={i} className={`ga-severity-badge ${SEVERITY_META[issue.severity]?.cls || ''}`}>
                      {SEVERITY_META[issue.severity]?.label || issue.severity} · {issue.section}
                    </span>
                  ))}
                </div>

                {!isDecided && (
                  isEditing
                    ? <textarea
                        className="ga-revision-body"
                        value={draft}
                        onChange={e => setRevisionDrafts(prev => ({ ...prev, [sectionName]: e.target.value }))}
                        rows={Math.max(6, draft.split('\n').length + 2)}
                        autoFocus
                      />
                    : <UnifiedDiff original={rev.original} revised={rev.revised} />
                )}
              </>)}
            </div>
          );
        })}
        {Object.values(revisionStatus).some(s => ['kept-revised', 'saved-edit'].includes(s)) && (
          <p className="ga-revision-summary">
            <FiCheck /> {Object.values(revisionStatus).filter(s => ['kept-revised', 'saved-edit'].includes(s)).length} section(s) staged — click <strong>Save</strong> above, then re-run Panel Review.
          </p>
        )}
      </div>
    );
  };

  // When used as panel_review stage, auto-run review if pending and no review exists.
  // Do NOT auto-run when we're only rendering the draft tab of the drafting stage.
  useEffect(() => {
    if (
      onAdvance && !readOnly &&
      proposal.current_stage === 'panel_review' &&
      !proposal.panel_review && !runningPanel
    ) {
      runPanelReview();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ga-done-stage">

      {/* Outcome tracker — only in done stage, not panel_review */}
      {!onAdvance && <div className="ga-outcome-bar">
        <span className="ga-outcome-label">Submission outcome:</span>
        <div className="ga-outcome-options">
          {Object.entries(OUTCOME_CONFIG).map(([val, cfg]) => (
            <button
              key={val}
              className={`ga-outcome-btn ${cfg.className} ${outcome === val ? 'ga-outcome-btn--active' : ''}`}
              onClick={() => {
                setOutcome(val);
                setOutcomeSaved(false);
                saveOutcome(val, outcomeScore);
              }}
              disabled={outcomeSaving}
            >
              {cfg.label}
            </button>
          ))}
        </div>
        {outcome === 'rejected' && (
          <div className="ga-outcome-score-wrap">
            <label className="ga-outcome-score-label">NIH score (1–9):</label>
            <input
              type="number" min="1" max="9"
              className="ga-outcome-score-input"
              value={outcomeScore}
              onChange={e => { setOutcomeScore(e.target.value); setOutcomeSaved(false); }}
              onBlur={() => outcomeScore !== '' && saveOutcome(outcome, outcomeScore)}
              placeholder="—"
            />
          </div>
        )}
        {outcomeSaved && <span className="ga-outcome-saved">✓ Saved</span>}
      </div>}

      {/* Corpus contribution — only shown once the user has recorded an outcome */}
      {!onAdvance && outcome && (outcome === 'funded' || outcome === 'rejected') && (
        <div className="ga-corpus-bar">
          <label className="ga-corpus-checkbox">
            <input
              type="checkbox"
              checked={contributing}
              disabled={contribSaving}
              onChange={e => toggleCorpusContribution(e.target.checked)}
            />
            <span className="ga-corpus-label">
              <strong>Contribute this submission to the reference corpus</strong>
              <span className="ga-corpus-hint">
                De-identified and used only to sharpen future reviewer panels.
                {consentStatus !== 'granted' && ' You\'ll be asked for consent first.'}
                {contributing && proposal.reference_proposal_id && ' Already ingested.'}
              </span>
            </span>
          </label>
        </div>
      )}

      {/* Advance button when used as an active pipeline stage */}
      {onAdvance && !readOnly && (
        <div className="ga-stage-actions" style={{ marginBottom: 16 }}>
          {runningPanel && <span className="ga-running-badge"><FiLoader className="ga-spin" /> Running 6-reviewer panel…</span>}
          {proposal.current_stage === 'drafting' && Object.keys(proposal.proposal_sections || {}).length > 0 && (
            <button className="ga-btn ga-btn--primary" onClick={() => onAdvance({ sections: edits })}>
              Continue to Panel Review <FiChevronRight />
            </button>
          )}
          {proposal.current_stage === 'panel_review' && proposal.panel_review && (
            <button className="ga-btn ga-btn--primary" onClick={() => onAdvance({})}>
              Continue to Compliance <FiChevronRight />
            </button>
          )}
          {proposal.current_stage === 'compliance' && proposal.compliance_report && (
            <button className="ga-btn ga-btn--primary" onClick={() => onAdvance({})}>
              Mark Complete <FiChevronRight />
            </button>
          )}
        </div>
      )}

      {/* Tab bar — hidden when the parent stepper controls which tab is shown */}
      {tabProp == null && (
        <div className="ga-done-tabs">
          <button className={`ga-tab ${tab === 'draft' ? 'ga-tab--active' : ''}`} onClick={() => setTab('draft')}>
            <FiFileText /> Proposal Draft
          </button>
          <button className={`ga-tab ${tab === 'compliance' ? 'ga-tab--active' : ''}`} onClick={() => setTab('compliance')}>
            <FiCheck /> Compliance Report
          </button>
          <button className={`ga-tab ${tab === 'panel' ? 'ga-tab--active' : ''}`} onClick={() => setTab('panel')}>
            <FiUsers /> Calibrated Review
          </button>
          <button className={`ga-tab ${tab === 'feedback' ? 'ga-tab--active' : ''}`} onClick={() => { setTab('feedback'); loadCritiques(); }}>
            <FiEdit2 /> Reviewer Feedback
          </button>
        </div>
      )}

      {/* ── Draft tab ── */}
      {tab === 'draft' && (
        <div className="ga-done-panel">
          <div className="ga-done-panel-header">
            <div>
              <h3>{proposal.title}</h3>
              {teamMembers.length > 1 && (
                <span className="ga-panel-tagline">
                  <FiUsers style={{ verticalAlign: 'middle' }} /> Shared with {teamMembers.length - 1} teammate{teamMembers.length !== 2 ? 's' : ''}
                </span>
              )}
              {canPickTeam && (
                <span style={{ position: 'relative', marginLeft: 8, display: 'inline-block' }}>
                  {proposal.team_id ? (
                    <span className="ga-panel-tagline" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      🏷️ {currentTeamName() || 'Team'}
                      <button className="ga-btn ga-btn--sm ga-btn--ghost"
                        title="Detach team" onClick={() => attachTeam(null)}>
                        <FiX />
                      </button>
                    </span>
                  ) : (
                    <>
                      <button className="ga-btn ga-btn--outline ga-btn--sm"
                        onClick={() => setShowTeamPicker(s => !s)}
                        title="Share this proposal with a team so teammates can collaborate on sections">
                        <FiUsers /> Share with team
                      </button>
                      {showTeamPicker && (
                        <div style={{ position: 'absolute', left: 0, top: '100%', zIndex: 20,
                          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                          padding: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 220 }}>
                          {userTeams.length === 0 && (
                            <p style={{ margin: 0, padding: 8, fontSize: 13, color: '#6b7280' }}>
                              You don't have any teams yet. Create one under Teams.
                            </p>
                          )}
                          {userTeams.map(t => (
                            <button key={t.id} className="ga-btn ga-btn--outline ga-btn--sm"
                              style={{ display: 'block', width: '100%', marginBottom: 4 }}
                              onClick={() => attachTeam(t.id)}>
                              <FiUsers /> {t.name}
                            </button>
                          ))}
                          <button className="ga-btn ga-btn--sm ga-btn--ghost"
                            style={{ display: 'block', width: '100%' }}
                            onClick={() => setShowTeamPicker(false)}>Cancel</button>
                        </div>
                      )}
                    </>
                  )}
                </span>
              )}
            </div>
            <div className="ga-done-actions">
              {teamMembers.length > 1 && !readOnly && (
                <div style={{ position: 'relative' }}>
                  <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => setShowRequestReview(s => !s)}>
                    <FiUsers /> Request Review
                  </button>
                  {showRequestReview && (
                    <div className="ga-review-modal" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 200 }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Request review from:</h4>
                      {teamMembers.filter(m => !m.is_owner).map(m => (
                        <button key={m.user_id} className="ga-btn ga-btn--outline ga-btn--sm" style={{ display: 'block', width: '100%', marginBottom: 4 }}
                          onClick={() => requestReview(m.user_id)}>
                          <FiUser /> {m.display_name || m.username}
                        </button>
                      ))}
                      <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={() => setShowRequestReview(false)}>Cancel</button>
                    </div>
                  )}
                </div>
              )}
              {hasUnsaved && (
                <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={saveSections} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              )}
              {['drafting', 'panel_review'].includes(proposal.current_stage) && !readOnly && onAdvance && (
                <button
                  className="ga-btn ga-btn--primary ga-btn--sm"
                  disabled={runningPanel || saving}
                  title={
                    hasUnsaved
                      ? 'Save accepted revisions and re-run the panel review on the new draft'
                      : proposal.panel_review
                        ? 'Jump to Panel Review and run a fresh pass'
                        : 'Run the 6-reviewer panel on the current draft'
                  }
                  onClick={async () => {
                    // From drafting: advance saves edits server-side and transitions
                    // to panel_review; the auto-run effect fires on remount.
                    if (proposal.current_stage === 'drafting') {
                      await onAdvance({ sections: edits });
                      return;
                    }
                    // From panel_review: save in place, then re-run.
                    if (hasUnsaved) {
                      const ok = await saveSections();
                      if (ok === false) return;
                    }
                    setTab('panel');
                    await runPanelReview();
                  }}
                >
                  {(runningPanel || saving)
                    ? <><FiLoader className="ga-spin" /> {saving ? 'Saving…' : 'Reviewing…'}</>
                    : hasUnsaved
                      ? <><FiPlay /> Save & Re-review</>
                      : proposal.panel_review
                        ? <><FiUsers /> Re-run Panel Review</>
                        : <><FiPlay /> Run Panel Review</>
                  }
                </button>
              )}
              <input ref={uploadRef} type="file" accept=".md,.docx" className="ga-upload-input"
                onChange={e => { if (e.target.files[0]) uploadDraft(e.target.files[0]); e.target.value = ''; }} />
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => uploadRef.current?.click()} disabled={uploading}>
                {uploading ? 'Uploading…' : <><FiDownload style={{transform:'rotate(180deg)'}} /> Upload</>}
              </button>
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => {
                const blob = new Blob([draftText], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `${proposal.title.replace(/\s+/g, '_')}_draft.md`; a.click();
                URL.revokeObjectURL(url);
              }}>
                <FiDownload /> .md
              </button>
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() =>
                downloadAsDocx(draftText, `${proposal.title.replace(/\s+/g, '_')}_draft.docx`)
              }>
                <FiDownload /> .docx
              </button>
            </div>
          </div>
          {(hasUnsaved || savedAt) && (
            <div className={`ga-save-hint${!hasUnsaved && savedAt ? ' ga-save-hint--saved' : ''}`}>
              {hasUnsaved ? '● Unsaved changes' : `✓ Saved at ${savedAt.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}`}
            </div>
          )}

          {proposal.collection_id && (
            <div className="ga-collection-badge">
              📚 Grounded in your collection — citations reference real papers
            </div>
          )}

          {renderRevisionsPanel()}

          {Object.entries(sections).map(([name, body]) => {
            const current = edits[name] !== undefined ? edits[name] : body;
            const isEditing = editingSection === name;
            const isExpanded = !!expandedSections[name];
            const toggleExpand = () => setExpanded(s => ({ ...s, [name]: !s[name] }));
            const assigneeId = sectionAssignments[name];
            const assignable = teamMembers.length > 1 && !readOnly;
            const assigneeOptions = teamMembers.filter(m => !m.is_owner);
            return (
              <div key={name} className={`ga-draft-section${isExpanded ? ' ga-draft-section--open' : ''}`}>
                <div className="ga-draft-section-header" onClick={toggleExpand} role="button" tabIndex={0}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleExpand()}>
                  <span className="ga-draft-section-chevron">
                    {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                  </span>
                  <h4>{name}</h4>
                  {assignable && assigneeId && (
                    <span style={{ position: 'relative', marginLeft: 'auto', marginRight: 8 }}
                      onClick={e => e.stopPropagation()}>
                      <button className="ga-btn ga-btn--sm ga-btn--outline"
                        title="Reassign or unassign this section"
                        onClick={() => setAssigningSection(assigningSection === name ? null : name)}>
                        <FiUser /> {teamMemberName(assigneeId)}
                      </button>
                      {assigningSection === name && (
                        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20,
                          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                          padding: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 200 }}>
                          {assigneeOptions.map(m => (
                            <button key={m.user_id} className="ga-btn ga-btn--outline ga-btn--sm"
                              style={{ display: 'block', width: '100%', marginBottom: 4 }}
                              onClick={() => assignSection(name, m.user_id)}>
                              <FiUser /> {m.display_name || m.username}
                            </button>
                          ))}
                          <button className="ga-btn ga-btn--sm ga-btn--ghost"
                            style={{ display: 'block', width: '100%' }}
                            onClick={() => assignSection(name, null)}>
                            <FiX /> Unassign
                          </button>
                        </div>
                      )}
                    </span>
                  )}
                  {assignable && !assigneeId && (
                    <span style={{ position: 'relative', marginLeft: 'auto', marginRight: 8 }}
                      onClick={e => e.stopPropagation()}>
                      <button className="ga-btn ga-btn--sm ga-btn--ghost"
                        title="Assign this section to a teammate"
                        onClick={() => setAssigningSection(assigningSection === name ? null : name)}>
                        <FiUsers /> Assign
                      </button>
                      {assigningSection === name && (
                        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20,
                          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                          padding: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 200 }}>
                          {assigneeOptions.map(m => (
                            <button key={m.user_id} className="ga-btn ga-btn--outline ga-btn--sm"
                              style={{ display: 'block', width: '100%', marginBottom: 4 }}
                              onClick={() => assignSection(name, m.user_id)}>
                              <FiUser /> {m.display_name || m.username}
                            </button>
                          ))}
                        </div>
                      )}
                    </span>
                  )}
                  {isExpanded && !isEditing && (
                    <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={e => { e.stopPropagation(); setEditing(name); }}>
                      <FiEdit2 /> Edit
                    </button>
                  )}
                  {isExpanded && isEditing && (
                    <button className="ga-btn ga-btn--sm ga-btn--ghost" onClick={e => { e.stopPropagation(); setEditing(null); }}>
                      <FiX /> Done
                    </button>
                  )}
                </div>
                {isExpanded && (
                  isEditing ? (
                    <textarea
                      className="ga-section-editor"
                      rows={12}
                      value={current}
                      onChange={e => setEdits(d => ({ ...d, [name]: e.target.value }))}
                    />
                  ) : (
                    <div className="ga-md-render">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{current}</ReactMarkdown>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Compliance tab ── */}
      {tab === 'compliance' && (
        <div className="ga-done-panel">
          <div className="ga-done-panel-header">
            <div>
              <h3>Panel Compliance Check</h3>
              {compliance?.isPanel && compliance.grantType && (
                <span className="ga-panel-tagline">Calibrated for {compliance.grantType}</span>
              )}
            </div>
            <div className="ga-done-actions">
              <select
                className="ga-panel-type-select"
                value={complianceGrantType}
                onChange={e => setComplianceGrantType(e.target.value)}
                disabled={rerunning}
                title="Select grant type — leave blank to auto-detect from NOFO"
              >
                <option value="">Auto-detect from NOFO</option>
                <optgroup label="NIH">
                  <option value="NIH R01">NIH R01</option>
                  <option value="NIH R21">NIH R21</option>
                  <option value="NIH R03">NIH R03</option>
                  <option value="NIH Career Development (K)">NIH K Award</option>
                  <option value="NIH">NIH (other)</option>
                </optgroup>
                <optgroup label="NSF">
                  <option value="NSF CAREER">NSF CAREER</option>
                  <option value="NSF">NSF (other)</option>
                </optgroup>
                <optgroup label="SBIR / STTR">
                  <option value="SBIR">SBIR</option>
                  <option value="STTR">STTR</option>
                </optgroup>
                <optgroup label="Other Federal">
                  <option value="DOE">DOE</option>
                  <option value="DOT">DOT</option>
                  <option value="federal research grant">General Federal</option>
                </optgroup>
              </select>
              <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={rerunCompliance} disabled={rerunning}>
                {rerunning ? <><span className="ga-spin">⟳</span> Running…</> : <><FiPlay /> Re-run Panel</>}
              </button>
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() => {
                const blob = new Blob([complianceMd], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `${proposal.title.replace(/\s+/g, '_')}_compliance.md`; a.click();
                URL.revokeObjectURL(url);
              }}>
                <FiDownload /> .md
              </button>
              <button className="ga-btn ga-btn--outline ga-btn--sm" onClick={() =>
                downloadAsDocx(complianceMd, `${proposal.title.replace(/\s+/g, '_')}_compliance.docx`)
              }>
                <FiDownload /> .docx
              </button>
            </div>
          </div>

          {compliance ? (
            <>
              {/* Overall status banner */}
              <div className={`ga-compliance-status ga-compliance-status--${statusClass}`}>
                <strong>{compliance.overall || 'Review complete'}</strong>
                {compliance.score && <div className="ga-compliance-score">{compliance.score}</div>}
              </div>

              {/* Summary */}
              {compliance.summary && (
                <p className="ga-compliance-summary">{compliance.summary}</p>
              )}

              {/* Failed — must fix */}
              {compliance.failed.length > 0 && (
                <div className="ga-compliance-section">
                  <h4 className="ga-compliance-section-title ga-compliance-section-title--fail">
                    ✗ Must Fix ({compliance.failed.length})
                  </h4>
                  {compliance.failed.map((item, i) => (
                    <div key={i} className="ga-checklist-item ga-checklist-item--fail">
                      <span className="ga-checklist-icon">✗</span>
                      <div>
                        <div className="ga-checklist-text"><strong>{item.text}</strong></div>
                        {item.issue && <div className="ga-checklist-issue">{item.issue}</div>}
                        {item.fix && <div className="ga-checklist-fix"><strong>Fix:</strong> {item.fix}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Needs review */}
              {compliance.review.length > 0 && (
                <div className="ga-compliance-section">
                  <h4 className="ga-compliance-section-title ga-compliance-section-title--warn">
                    ⚠ Needs Review ({compliance.review.length})
                  </h4>
                  {compliance.review.map((item, i) => (
                    <div key={i} className="ga-checklist-item ga-checklist-item--warn">
                      <span className="ga-checklist-icon">⚠</span>
                      <div>
                        <div className="ga-checklist-text"><strong>{item.text || item}</strong></div>
                        {item.issue && <div className="ga-checklist-issue">{item.issue}</div>}
                        {item.fix && <div className="ga-checklist-fix"><strong>Action:</strong> {item.fix}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Passed */}
              {compliance.passed.length > 0 && (
                <div className="ga-compliance-section">
                  <h4 className="ga-compliance-section-title ga-compliance-section-title--pass">
                    ✓ Satisfied ({compliance.passed.length})
                  </h4>
                  {compliance.passed.map((item, i) => (
                    <div key={i} className="ga-checklist-item ga-checklist-item--pass">
                      <span className="ga-checklist-icon">✓</span>
                      <div>
                        <div className="ga-checklist-text"><strong>{item.text || item}</strong></div>
                        {item.evidence && <div className="ga-checklist-issue">{item.evidence}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Remaining placeholders */}
              {compliance.placeholders.length > 0 && (
                <div className="ga-compliance-section">
                  <h4 className="ga-compliance-section-title">Remaining Placeholders</h4>
                  {compliance.placeholders.map((item, i) => (
                    <div key={i} className="ga-checklist-item ga-checklist-item--placeholder">
                      <code className="ga-placeholder-tag">[…]</code>
                      <div className="ga-checklist-text">{item}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Next steps */}
              {compliance.nextSteps && (
                <div className="ga-compliance-section">
                  <h4 className="ga-compliance-section-title">Recommended Next Steps</h4>
                  <ol className="ga-nextsteps-list">
                    {compliance.nextSteps.split('\n').filter(Boolean).map((step, i) => (
                      <li key={i}>{step.replace(/^\d+\.\s*/, '')}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Final note */}
              {compliance.finalNote && (
                <p className="ga-compliance-finalnote">{compliance.finalNote}</p>
              )}

              {/* Fallback: if parsing produced nothing useful, show raw MD */}
              {!compliance.passed.length && !compliance.failed.length && !compliance.review.length && (
                <div className="ga-md-render">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{complianceMd}</ReactMarkdown>
                </div>
              )}

              {/* ── Per-specialist accordion (panel format only) ── */}
              {compliance.isPanel && compliance.reviewers?.length > 0 && (
                <div className="ga-compliance-specialists">
                  <h4 className="ga-compliance-section-title">Specialist Breakdown</h4>
                  {compliance.reviewers.map(reviewer => {
                    const isExpanded = !!expandedComplianceReviewers[reviewer.id];
                    const failCount = (reviewer.failed || []).length;
                    const warnCount = (reviewer.warnings || []).length;
                    const passCount = (reviewer.passed || []).length;
                    return (
                      <div key={reviewer.id} className="ga-compliance-specialist-card">
                        <button
                          className="ga-compliance-specialist-header"
                          onClick={() => setExpandedComplianceReviewers(prev => ({
                            ...prev, [reviewer.id]: !prev[reviewer.id]
                          }))}
                        >
                          <span className="ga-compliance-specialist-label">{reviewer.label}</span>
                          <span className="ga-compliance-specialist-badges">
                            {failCount > 0 && (
                              <span className="ga-compliance-count-badge ga-compliance-count-badge--fail">
                                ✗ {failCount}
                              </span>
                            )}
                            {warnCount > 0 && (
                              <span className="ga-compliance-count-badge ga-compliance-count-badge--warn">
                                ⚠ {warnCount}
                              </span>
                            )}
                            {passCount > 0 && (
                              <span className="ga-compliance-count-badge ga-compliance-count-badge--pass">
                                ✓ {passCount}
                              </span>
                            )}
                          </span>
                          <span className="ga-compliance-specialist-chevron">{isExpanded ? '▲' : '▼'}</span>
                        </button>
                        {isExpanded && (
                          <div className="ga-compliance-specialist-body">
                            {reviewer.summary && (
                              <p className="ga-compliance-specialist-summary">{reviewer.summary}</p>
                            )}
                            {(reviewer.failed || []).map((item, i) => {
                              const fb = getComplianceFeedback(reviewer.id, 'failed', i);
                              return (
                                <div key={i} className={`ga-checklist-item ga-checklist-item--fail${fb?.verdict === 'disagree' ? ' ga-issue--disagreed' : fb?.verdict === 'agree' ? ' ga-issue--agreed' : ''}`}>
                                  <span className="ga-checklist-icon">✗</span>
                                  <div style={{ flex: 1 }}>
                                    <div className="ga-checklist-text"><strong>{item.text}</strong></div>
                                    {item.issue && <div className="ga-checklist-issue">{item.issue}</div>}
                                    {item.fix && <div className="ga-checklist-fix"><strong>Fix:</strong> {item.fix}</div>}
                                    <FeedbackButtons
                                      feedback={fb}
                                      onFeedback={f => saveComplianceFeedback(reviewer.id, 'failed', i, f)}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {(reviewer.warnings || []).map((item, i) => {
                              const fb = getComplianceFeedback(reviewer.id, 'warnings', i);
                              return (
                                <div key={i} className={`ga-checklist-item ga-checklist-item--warn${fb?.verdict === 'disagree' ? ' ga-issue--disagreed' : fb?.verdict === 'agree' ? ' ga-issue--agreed' : ''}`}>
                                  <span className="ga-checklist-icon">⚠</span>
                                  <div style={{ flex: 1 }}>
                                    <div className="ga-checklist-text"><strong>{item.text}</strong></div>
                                    {item.issue && <div className="ga-checklist-issue">{item.issue}</div>}
                                    {item.fix && <div className="ga-checklist-fix"><strong>Action:</strong> {item.fix}</div>}
                                    <FeedbackButtons
                                      feedback={fb}
                                      onFeedback={f => saveComplianceFeedback(reviewer.id, 'warnings', i, f)}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {(reviewer.passed || []).map((item, i) => (
                              <div key={i} className="ga-checklist-item ga-checklist-item--pass">
                                <span className="ga-checklist-icon">✓</span>
                                <div>
                                  <div className="ga-checklist-text"><strong>{item.text}</strong></div>
                                  {item.evidence && <div className="ga-checklist-issue">{item.evidence}</div>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <p className="ga-hint">No compliance report available.</p>
          )}
        </div>
      )}

      {/* ── Calibrated Review tab ── */}
      {tab === 'panel' && (
        <div className="ga-done-panel">
          <div className="ga-done-panel-header">
            <div>
              <h3>Calibrated Review</h3>
              <span className="ga-panel-tagline">Reviewers who've done their homework</span>
            </div>
            <div className="ga-done-actions">
              <select
                className="ga-panel-type-select"
                value={panelGrantType}
                onChange={e => setPanelGrantType(e.target.value)}
                disabled={runningPanel}
                title="Select grant type — leave blank to auto-detect from NOFO"
              >
                <option value="">Auto-detect from NOFO</option>
                <optgroup label="NIH">
                  <option value="NIH R01">NIH R01</option>
                  <option value="NIH R21">NIH R21</option>
                  <option value="NIH R03">NIH R03</option>
                  <option value="NIH Career Development (K)">NIH K Award</option>
                  <option value="NIH">NIH (other)</option>
                </optgroup>
                <optgroup label="NSF">
                  <option value="NSF CAREER">NSF CAREER</option>
                  <option value="NSF">NSF (other)</option>
                </optgroup>
                <optgroup label="SBIR / STTR">
                  <option value="SBIR">SBIR</option>
                  <option value="STTR">STTR</option>
                </optgroup>
                <optgroup label="Other Federal">
                  <option value="DOE">DOE</option>
                  <option value="DOT">DOT</option>
                  <option value="federal research grant">General Federal</option>
                </optgroup>
              </select>
              {proposal.panel_review && (
                <>
                  <button
                    className={`ga-btn ga-btn--sm ${feedbackSubmitted ? 'ga-btn--success' : 'ga-btn--outline'}`}
                    onClick={submitAllFeedback}
                    disabled={feedbackSubmitting || runningPanel}
                    title="Persist all agree/disagree feedback to the server"
                  >
                    {feedbackSubmitting
                      ? <><FiLoader className="ga-spin" /> Saving…</>
                      : feedbackSubmitted
                        ? <><FiCheck /> Saved</>
                        : 'Submit Feedback'
                    }
                  </button>
                  <button
                    className="ga-btn ga-btn--outline ga-btn--sm"
                    onClick={() => {
                      const md = formatPanelReviewReport(proposal.panel_review, reviewFeedback, proposal.title);
                      const blob = new Blob([md], { type: 'text/markdown' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `${(proposal.title || 'review').replace(/\s+/g, '_')}_calibrated_review.md`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    }}
                    title="Download report as Markdown"
                  >
                    <FiDownload /> .md
                  </button>
                  <button
                    className="ga-btn ga-btn--outline ga-btn--sm"
                    onClick={() => {
                      const md = formatPanelReviewReport(proposal.panel_review, reviewFeedback, proposal.title);
                      downloadAsDocx(md, `${(proposal.title || 'review').replace(/\s+/g, '_')}_calibrated_review.docx`);
                    }}
                    title="Download report as Word document"
                  >
                    <FiDownload /> .docx
                  </button>
                  <button
                    className="ga-btn ga-btn--outline ga-btn--sm"
                    onClick={runIteration}
                    disabled={iterating || runningPanel}
                    title="Rewrite sections to address reviewer issues"
                  >
                    {iterating
                      ? <><FiLoader className="ga-spin" /> Iterating…</>
                      : <><FiEdit2 /> Iterate Draft</>
                    }
                  </button>
                  {onAdvance && !readOnly && proposal.current_stage === 'panel_review' && (
                    <button
                      className="ga-btn ga-btn--outline ga-btn--sm"
                      onClick={() => onAdvance({})}
                      disabled={runningPanel || iterating}
                      title="Lock in this review and move on to compliance"
                    >
                      Continue to Compliance <FiChevronRight />
                    </button>
                  )}
                </>
              )}
              <button
                className="ga-btn ga-btn--primary ga-btn--sm"
                onClick={runPanelReview}
                disabled={runningPanel}
              >
                {runningPanel
                  ? <><FiLoader className="ga-spin" /> Reviewing…</>
                  : proposal.panel_review
                    ? <><FiPlay /> Re-run</>
                    : <><FiPlay /> Run Review</>
                }
              </button>
            </div>
          </div>

          {!proposal.panel_review && !runningPanel && (
            <div className="ga-panel-empty">
              <p className="ga-panel-empty-lead">
                Six experts stress-test your proposal — each one has <strong>read your NOFO</strong> and your literature before writing a single word of feedback.
              </p>
              <div className="ga-panel-adaptive-note">
                Panel composition adapts to your grant type:
                <div className="ga-panel-type-chips">
                  {[
                    { label: 'NIH R01/R21', desc: 'Significance · Innovation · Approach · Investigator · Program Officer · Skeptic' },
                    { label: 'NIH K Award', desc: 'Candidate · Mentor/Env · Research Plan · Training · Program Officer · Skeptic' },
                    { label: 'NSF',         desc: 'Intellectual Merit · Broader Impacts · Methods · Feasibility · Program Officer · Skeptic' },
                    { label: 'SBIR/STTR',   desc: 'Technical Feasibility · Commercial Potential · Team · Innovation/IP · Agency · Skeptic' },
                    { label: 'DOE / DOT',   desc: 'Scientific Merit · Approach · Feasibility · Broader Impact · Program Officer · Skeptic' },
                  ].map(({ label, desc }) => (
                    <div key={label} className="ga-panel-type-chip">
                      <span className="ga-panel-type-chip-label">{label}</span>
                      <span className="ga-panel-type-chip-desc">{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {runningPanel && (
            <div className="ga-panel-running">
              <FiLoader className="ga-spin ga-panel-running-icon" />
              <p>Selecting panel for your grant type and running six reviewers in parallel…</p>
              <p className="ga-hint">Each reviewer has access to your NOFO and literature. This takes 30–60 seconds.</p>
            </div>
          )}

          {proposal.panel_review && !runningPanel && (() => {
            const pr = proposal.panel_review;
            const synthesis = pr.synthesis || {};
            const reviewers = pr.reviewers || [];
            const rankedIssues = synthesis.ranked_issues || [];
            const readiness = synthesis.overall_readiness || '';
            const readinessClass = readiness.includes('Ready to Submit') ? 'ready'
              : readiness.includes('Minor') ? 'minor'
              : readiness.includes('Major') ? 'major'
              : 'not-ready';

            return (
              <>
                {/* Readiness banner */}
                <div className={`ga-panel-readiness ga-panel-readiness--${readinessClass}`}>
                  <strong>{readiness}</strong>
                  {pr.grant_type && <span className="ga-panel-grant-type">{pr.grant_type}</span>}
                  {pr.criteria_source && (
                    <span className="ga-panel-grant-type" style={{ background: '#d1fae5', color: '#065f46' }}>
                      {pr.criteria_source === 'user_edited' ? 'Custom criteria' :
                       pr.criteria_source === 'corpus_calibrated' ? 'Calibrated from corpus' :
                       pr.criteria_source === 'feedback_adjusted' ? 'Feedback-adjusted' :
                       pr.criteria_source === 'nofo_only' ? 'Calibrated from NOFO' : 'Calibrated'}
                    </span>
                  )}
                </div>

                {/* Top strengths */}
                {synthesis.top_strengths?.length > 0 && (
                  <div className="ga-panel-section">
                    <h4 className="ga-panel-section-title ga-panel-section-title--strengths">
                      ✓ Panel Strengths
                    </h4>
                    <ul className="ga-panel-strengths-list">
                      {synthesis.top_strengths.map((s, i) => {
                        const fb = getReviewFeedback('__synthesis__', 'top_strengths', i);
                        return (
                          <li key={i} className={fb?.verdict === 'disagree' ? 'ga-issue--disagreed' : fb?.verdict === 'agree' ? 'ga-issue--agreed' : ''}>
                            <span>{s}</span>
                            <FeedbackButtons
                              feedback={fb}
                              onFeedback={f => saveReviewFeedback('__synthesis__', 'top_strengths', i, f)}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Ranked issues */}
                {rankedIssues.length > 0 && (
                  <div className="ga-panel-section">
                    <h4 className="ga-panel-section-title ga-panel-section-title--issues">
                      Priority Issues ({rankedIssues.length})
                    </h4>
                    {rankedIssues.map((issue, i) => {
                      const fb = getReviewFeedback('__synthesis__', 'ranked_issues', i);
                      return (
                        <div key={i} className={`ga-panel-issue ga-panel-issue--${issue.severity}${fb?.verdict === 'disagree' ? ' ga-issue--disagreed' : fb?.verdict === 'agree' ? ' ga-issue--agreed' : ''}`}>
                          <div className="ga-panel-issue-header">
                            <span className={`ga-severity-badge ${SEVERITY_META[issue.severity]?.cls || ''}`}>
                              {SEVERITY_META[issue.severity]?.label || issue.severity}
                            </span>
                            <span className="ga-panel-issue-section">{issue.section}</span>
                            <div className="ga-panel-flagged-by">
                              {(issue.flagged_by || []).map(rid => {
                                const c = REVIEWER_COLORS[rid] || {};
                                return (
                                  <span key={rid} className="ga-reviewer-dot" style={{ background: c.dot }} title={rid.replace(/_/g, ' ')} />
                                );
                              })}
                            </div>
                          </div>
                          <p className="ga-panel-issue-text">{issue.issue}</p>
                          {issue.fix && <p className="ga-panel-issue-fix"><strong>Fix:</strong> {issue.fix}</p>}
                          <FeedbackButtons
                            feedback={fb}
                            onFeedback={f => saveReviewFeedback('__synthesis__', 'ranked_issues', i, f)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Individual reviewer cards */}
                <div className="ga-panel-section">
                  <h4 className="ga-panel-section-title">Individual Reviewer Feedback</h4>
                  {reviewers.map(reviewer => {
                    const c = REVIEWER_COLORS[reviewer.id] || { bg: '#f9fafb', border: '#e5e7eb', text: '#374151', dot: '#6b7280' };
                    const isOpen = !!expandedReviewers[reviewer.id];
                    const highCount = (reviewer.issues || []).filter(i => i.severity === 'high').length;
                    return (
                      <div key={reviewer.id} className="ga-reviewer-card" style={{ borderColor: c.border }}>
                        <div
                          className="ga-reviewer-card-header"
                          style={{ background: c.bg }}
                          onClick={() => setExpandedReviewers(s => ({ ...s, [reviewer.id]: !s[reviewer.id] }))}
                          role="button" tabIndex={0}
                          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setExpandedReviewers(s => ({ ...s, [reviewer.id]: !s[reviewer.id] }))}
                        >
                          <span className="ga-reviewer-dot-lg" style={{ background: c.dot }} />
                          <span className="ga-reviewer-label" style={{ color: c.text }}>{reviewer.label}</span>
                          {reviewer.id === 'program_officer' && proposal.nofo_text && (
                            <span className="ga-reviewer-context-badge">read your NOFO</span>
                          )}
                          {reviewer.id === 'domain_expert' && (
                            <span className="ga-reviewer-context-badge">read your literature</span>
                          )}
                          {highCount > 0 && (
                            <span className="ga-reviewer-high-count">{highCount} high</span>
                          )}
                          <span className="ga-reviewer-issue-count">{(reviewer.issues || []).length} issue{(reviewer.issues || []).length !== 1 ? 's' : ''}</span>
                          <span className="ga-reviewer-chevron" style={{ color: c.text }}>
                            {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                          </span>
                        </div>
                        {isOpen && (
                          <div className="ga-reviewer-card-body">
                            {reviewer.overall_comment && (
                              <p className="ga-reviewer-comment">{reviewer.overall_comment}</p>
                            )}
                            {(reviewer.strengths || []).length > 0 && (
                              <div className="ga-reviewer-strengths">
                                <strong>Strengths:</strong>
                                <ul>{reviewer.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                              </div>
                            )}
                            {(reviewer.issues || []).map((issue, i) => {
                              const fb = getReviewFeedback(reviewer.id, 'issues', i);
                              return (
                                <div key={i} className={`ga-panel-issue ga-panel-issue--${issue.severity}${fb?.verdict === 'disagree' ? ' ga-issue--disagreed' : fb?.verdict === 'agree' ? ' ga-issue--agreed' : ''}`}>
                                  <div className="ga-panel-issue-header">
                                    <span className={`ga-severity-badge ${SEVERITY_META[issue.severity]?.cls || ''}`}>
                                      {SEVERITY_META[issue.severity]?.label || issue.severity}
                                    </span>
                                    <span className="ga-panel-issue-section">{issue.section}</span>
                                  </div>
                                  <p className="ga-panel-issue-text">{issue.issue}</p>
                                  {issue.fix && <p className="ga-panel-issue-fix"><strong>Fix:</strong> {issue.fix}</p>}
                                  <FeedbackButtons
                                    feedback={fb}
                                    onFeedback={f => saveReviewFeedback(reviewer.id, 'issues', i, f)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Revisions live on the Draft step — surface a hint here when iterating. */}
                {iterating && (
                  <div className="ga-revision-loading">
                    <FiLoader className="ga-spin" />
                    <span>Rewriting sections — switching to the Draft step…</span>
                  </div>
                )}
                {revisions && Object.keys(revisions).length === 0 && !iterating && (
                  <div className="ga-revision-empty">
                    No sections could be matched to the reviewer issues. Try running the panel review again or editing sections manually.
                  </div>
                )}
                {revisions && Object.keys(revisions).length > 0 && !iterating && (
                  <div className="ga-revision-empty">
                    Revisions ready in the <button className="ga-btn-link" onClick={() => setTab('draft')}>Draft step</button>.
                  </div>
                )}

                <p className="ga-panel-footer-note">
                  Run at {new Date(pr.run_at).toLocaleString()} · Calibrated to your NOFO and literature — not generic role-play. Not a score prediction.
                </p>
              </>
            );
          })()}
        </div>
      )}

      {/* ── Reviewer Feedback tab ── */}
      {tab === 'feedback' && (
        <div className="ga-done-panel">
          <div className="ga-done-panel-header">
            <h3>Reviewer Feedback</h3>
          </div>

          <p className="ga-feedback-desc">
            Paste the reviewer comments you received after submission. Mnevia will extract
            recurring critique patterns across all your proposals and pre-empt them in future drafts.
          </p>

          <textarea
            className="ga-feedback-textarea"
            placeholder="Paste reviewer comments here…"
            value={reviewerFeedbackText}
            onChange={e => { setReviewerFeedbackText(e.target.value); setFeedbackSavedMsg(''); }}
          />

          <div className="ga-feedback-actions">
            <button
              className="ga-btn ga-btn--primary ga-btn--sm"
              onClick={saveReviewerFeedback}
              disabled={feedbackSaving || !reviewerFeedbackText.trim()}
            >
              {feedbackSaving ? <><FiLoader className="ga-spin" /> Saving…</> : 'Save & Analyze'}
            </button>
            {feedbackSavedMsg && <span className="ga-outcome-saved">{feedbackSavedMsg}</span>}
          </div>

          {critiques && critiques.length > 0 && (
            <div className="ga-critiques">
              <div className="ga-critiques-title">Recurring critique themes across your proposals</div>
              {critiques.map((c, i) => (
                <div key={i} className="ga-critique-card">
                  <div className="ga-critique-header">
                    <span className="ga-critique-theme">{c.theme}</span>
                    <span className="ga-critique-freq">{c.frequency} proposal{c.frequency !== 1 ? 's' : ''}</span>
                  </div>
                  <p className="ga-critique-desc">{c.description}</p>
                  <p className="ga-critique-remedy"><strong>Fix:</strong> {c.remedy}</p>
                </div>
              ))}
            </div>
          )}

          {critiques && critiques.length === 0 && (
            <div className="ga-critiques-empty">
              No recurring themes detected yet — add feedback from more proposals to surface patterns.
            </div>
          )}
        </div>
      )}

      <ConsentModal
        isOpen={showConsentModal}
        onClose={() => setShowConsentModal(false)}
        onDecision={(decision) => {
          setConsentStatus(decision);
          if (decision === 'granted') {
            // Immediately flip the checkbox on; the toggle handler will persist it.
            toggleCorpusContribution(true);
          }
        }}
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function GrantAgent({ initialCollectionId = null, initialIdea = null, initialProposalId = null, onMounted, user = null } = {}) {
  const [proposals, setProposals]           = useState([]);
  const [selected, setSelected]             = useState(null);
  const [loading, setLoading]               = useState(false);
  const [stageLoading, setStageLoading]     = useState(false);
  const [showCreate, setShowCreate]         = useState(false);
  const [newTitle, setNewTitle]             = useState('');
  const [error, setError]                   = useState(null);
  const [viewStage, setViewStage]           = useState(null);
  const [editingStage, setEditingStage]     = useState(null);
  const viewMode = 'pipeline'; // document view removed — pipeline handles all stages including panel review
  const [collections, setCollections]       = useState([]);
  const [careerStage, setCareerStage]       = useState(null); // 'esi' | 'established' | null
  const [styleFingerprint, setStyleFingerprint] = useState(null);
  const [grantHistoryCount, setGrantHistoryCount] = useState(0);
  const [winPatterns, setWinPatterns]       = useState(null);
  const [showStageHints, setShowStageHints] = useState(() => !isGuideSeen(user?.id, 'stage_hints'));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('ga_sidebar_collapsed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('ga_sidebar_collapsed', sidebarCollapsed ? '1' : '0'); } catch {}
  }, [sidebarCollapsed]);

  // Reset view and edit state when switching proposals; auto-switch to document view if draft exists
  useEffect(() => {
    setViewStage(null);
    setEditingStage(null);
    // viewMode is always 'pipeline' now
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchProposals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/grant-proposals'), { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load proposals');
      const data = await res.json();
      setProposals(data);
      if (selected) {
        const fresh = data.find(p => p.id === selected.id);
        if (fresh) setSelected(fresh);
      }
      return data;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [selected?.id]);

  useEffect(() => {
    // Fetch user's collections for the NOFO stage collection picker
    fetch(getApiEndpoint('/api/collections'), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCollections(Array.isArray(data) ? data : []))
      .catch(() => {});
    // Fetch PI profile for career stage + flywheel banner
    fetch(getApiEndpoint('/api/grant-proposals/pi-profile'), { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(profile => {
        if (!profile) return;
        if (profile.career_stage) setCareerStage(profile.career_stage);
        if (profile.style_fingerprint) setStyleFingerprint(profile.style_fingerprint);
        if (Array.isArray(profile.grant_history)) setGrantHistoryCount(profile.grant_history.length);
        if (profile.win_patterns) setWinPatterns(profile.win_patterns);
      })
      .catch(() => {});
    // If launched from NOFO discovery, select the pre-created proposal directly
    if (initialProposalId) {
      fetchProposals();
      fetch(getApiEndpoint(`/api/grant-proposals/${initialProposalId}`), { headers: getAuthHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(p => {
          if (p) {
            setSelected(p);
            setProposals(prev => prev.find(x => x.id === p.id) ? prev : [p, ...prev]);
          }
        })
        .catch(() => {});
    } else if (initialCollectionId) {
      // If launched from the Collections page, open the create form immediately
      fetchProposals();
      setShowCreate(true);
    } else {
      // Arrived from dashboard or direct nav — auto-open create form if no proposals yet
      fetchProposals().then(ps => {
        if (ps.length === 0) setShowCreate(true);
      });
    }
    if (onMounted) onMounted();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProposal = useCallback(async (id) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${id}`), { headers: getAuthHeaders() });
      if (!res.ok) return;
      const fresh = await res.json();
      setSelected(fresh);
      setProposals(ps => ps.map(p => p.id === id ? fresh : p));
    } catch (_) {}
  }, []);

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const createProposal = async () => {
    if (!newTitle.trim()) return;
    try {
      const res = await fetch(getApiEndpoint('/api/grant-proposals'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle }),
      });
      if (!res.ok) throw new Error('Failed to create proposal');
      const p = await res.json();
      setProposals(prev => [p, ...prev]);
      setSelected(p);
      setShowCreate(false);
      setNewTitle('');
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteProposal = async (id) => {
    if (!window.confirm('Delete this proposal?')) return;
    await fetch(getApiEndpoint(`/api/grant-proposals/${id}`), {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    setProposals(prev => prev.filter(p => p.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  // ── Stage actions ─────────────────────────────────────────────────────────

  const runStage = async () => {
    if (!selected) return;
    setStageLoading(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${selected.id}/run`), {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Stage run failed');
      const updated = await res.json();
      setSelected(updated);
      setProposals(prev => prev.map(p => p.id === updated.id ? updated : p));
    } catch (err) {
      setError(err.message);
    } finally {
      setStageLoading(false);
    }
  };

  const resetStage = async () => {
    if (!selected) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${selected.id}/reset`), {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Reset failed (${res.status})`);
      }
      const updated = await res.json();
      setSelected(updated);
      setProposals(prev => prev.map(p => p.id === updated.id ? updated : p));
    } catch (err) {
      setError(err.message);
    }
  };

  const advanceStage = async (data) => {
    if (!selected) return;
    setStageLoading(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${selected.id}/advance`), {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ data }),
      });
      if (!res.ok) throw new Error('Failed to advance stage');
      const updated = await res.json();
      setSelected(updated);
      setProposals(prev => prev.map(p => p.id === updated.id ? updated : p));
      // A pinned past-stage view would otherwise shadow the new current stage.
      setViewStage(null);
      setEditingStage(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setStageLoading(false);
    }
  };

  const rewindStage = async (stageKey, data = {}) => {
    if (!selected) return;
    setStageLoading(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/grant-proposals/${selected.id}/rewind`), {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage_key: stageKey, data }),
      });
      if (!res.ok) throw new Error('Failed to rewind stage');
      const updated = await res.json();
      setSelected(updated);
      setProposals(prev => prev.map(p => p.id === updated.id ? updated : p));
      setViewStage(null);
      setEditingStage(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setStageLoading(false);
    }
  };

  // ── Stage renderer ────────────────────────────────────────────────────────

  const renderStage = () => {
    if (!selected) return null;
    const stage    = viewStage || selected.current_stage;
    const stageObj = STAGES.find(s => s.key === stage);
    if (!stageObj) return null;

    // isPast: viewing a step that has already been completed
    const isPast = viewStage && (
      selected.current_stage === 'done' || stageIndex(viewStage) < stageIndex(selected.current_stage)
    );

    const isAutoStage = stageObj.type === 'auto';
    const isEditing   = isPast && editingStage === stage;

    // Read-only unless it's the active stage or we're in edit mode
    const readOnly = isPast && !isEditing;
    // For past (non-editing) stages force stage_status to 'done' so outputs render
    const p = readOnly ? { ...selected, stage_status: 'done' } : selected;

    const advanceFn = isEditing ? (data) => rewindStage(stage, data) : advanceStage;

    // Unified draft / panel-review / compliance view — mirrors the paper pipeline:
    // clicking the "Draft" step opens the draft tab, "Panel Review" opens the panel
    // tab, "Compliance" opens the compliance tab. Compliance only joins once its
    // report exists; before that we fall through to ComplianceStage so that
    // AutoStageShell kicks off the run.
    const hasDraftSections = Object.keys(selected.proposal_sections || {}).length > 0;
    const complianceReady  = !!selected.compliance_report;
    const unifiedStages = ['drafting', 'panel_review'];
    if (complianceReady) unifiedStages.push('compliance');
    const canUseUnified = unifiedStages.includes(selected.current_stage) && hasDraftSections;
    if (canUseUnified && unifiedStages.includes(stage) && !isEditing) {
      const stageToTab = { drafting: 'draft', panel_review: 'panel', compliance: 'compliance' };
      const tabToStage = { draft: 'drafting', panel: 'panel_review', compliance: 'compliance' };
      // Within the draft↔panel-review iteration loop, clicking between the Draft
      // and Panel Review steps is navigation, not rewinding — don't treat it as
      // read-only. Compliance is the only step in the unified handler that can
      // legitimately be a past stage.
      const loopReadOnly = stage === 'compliance' ? readOnly : false;
      return (
        <DoneStage
          key={selected.current_stage}
          proposal={loopReadOnly ? p : selected}
          onRefresh={fetchProposal}
          user={user}
          onAdvance={loopReadOnly ? null : advanceStage}
          readOnly={loopReadOnly}
          tab={stageToTab[stage]}
          onTabChange={(t) => setViewStage(tabToStage[t] || null)}
        />
      );
    }

    let stageEl;
    switch (stage) {
      case 'nofo_input':      stageEl = <NofoInputStage proposal={p} onAdvance={advanceFn} readOnly={readOnly} rewindMode={isEditing} collections={collections} initialCollectionId={initialCollectionId} initialIdea={initialIdea} />; break;
      case 'funder_research': stageEl = <FunderResearchStage proposal={p} onRun={runStage} onReset={resetStage} onAdvance={advanceFn} readOnly={readOnly} onProposalUpdate={(fresh) => { setSelected(fresh); setProposals(ps => ps.map(x => x.id === fresh.id ? fresh : x)); }} />; break;
      case 'pi_profile':      stageEl = <PiProfileStage proposal={p} onAdvance={advanceFn} readOnly={readOnly} rewindMode={isEditing} />; break;
      case 'team_building':   stageEl = <TeamBuildingStage proposal={p} onRun={runStage} onReset={resetStage} onAdvance={advanceFn} readOnly={readOnly} />; break;
      case 'drafting':        stageEl = <DraftingStage proposal={p} onRun={runStage} onReset={resetStage} onAdvance={advanceFn} readOnly={readOnly} />; break;
      case 'panel_review':    stageEl = <DoneStage proposal={p} onRefresh={fetchProposal} user={user} onAdvance={advanceFn} defaultTab="panel" readOnly={readOnly} />; break;
      case 'compliance':      stageEl = <ComplianceStage proposal={p} onRun={runStage} onReset={resetStage} onAdvance={advanceFn} readOnly={readOnly} />; break;
      case 'done':            stageEl = <DoneStage proposal={p} onRefresh={fetchProposal} user={user} />; break;
      default:                return null;
    }

    if (!isPast || stage === 'done') return stageEl;

    return (
      <div className="ga-past-stage-wrapper">
        {isEditing ? (
          <div className="ga-rewind-banner">
            <FiEdit2 /> Editing — saving will reset all later stages
            <button className="ga-btn ga-btn--sm ga-btn--outline" onClick={() => setEditingStage(null)}>
              Cancel
            </button>
          </div>
        ) : (
          <div className="ga-past-stage-actions">
            {isAutoStage ? (
              <button
                className="ga-btn ga-btn--outline ga-btn--sm"
                disabled={stageLoading}
                onClick={() => rewindStage(stage)}
              >
                <FiPlay /> Re-run from here
              </button>
            ) : (
              <button
                className="ga-btn ga-btn--outline ga-btn--sm"
                onClick={() => setEditingStage(stage)}
              >
                <FiEdit2 /> Edit this step
              </button>
            )}
          </div>
        )}
        {stageEl}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="ga-page ph-no-capture">
      {/* Left panel — proposal list */}
      <div className={`ga-sidebar ${sidebarCollapsed ? 'ga-sidebar--collapsed' : ''}`}>
        <div className="ga-sidebar-header">
          {!sidebarCollapsed && <h2>Grant Agent</h2>}
          {!sidebarCollapsed && (
            <button className="ga-btn ga-btn--primary ga-btn--sm" onClick={() => setShowCreate(true)}>
              <FiPlus /> New
            </button>
          )}
          <button className="ga-sidebar-toggle" onClick={() => setSidebarCollapsed(s => !s)}
            title={sidebarCollapsed ? 'Show proposal list' : 'Hide proposal list'}>
            {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>

        {error && <div className="ga-error">{error}</div>}

        {loading && !proposals.length ? (
          <div className="ga-sidebar-loading"><FiLoader className="spin" /></div>
        ) : proposals.length === 0 ? (
          <div className="ga-sidebar-empty">
            <p>No proposals yet.</p>
            <button className="ga-btn ga-btn--outline" onClick={() => setShowCreate(true)}>
              <FiPlus /> Start a proposal
            </button>
          </div>
        ) : (
          <ul className="ga-proposal-list">
            {proposals.map(p => (
              <li
                key={p.id}
                className={`ga-proposal-item ${selected?.id === p.id ? 'ga-proposal-item--active' : ''}`}
                onClick={() => setSelected(p)}
              >
                <div className="ga-proposal-item-body">
                  <span className="ga-proposal-title">{p.title}</span>
                  <span className="ga-proposal-stage">
                    {STAGES.find(s => s.key === p.current_stage)?.label}
                    {p.stage_status === 'running' && <FiLoader className="spin" style={{ marginLeft: 4 }} />}
                    {p.current_stage === 'done' && <FiCheck style={{ marginLeft: 4, color: 'var(--color-success, #22c55e)' }} />}
                  </span>
                </div>
                <button
                  className="ga-delete-btn"
                  onClick={e => { e.stopPropagation(); deleteProposal(p.id); }}
                  title="Delete"
                >
                  <FiTrash2 />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right panel — proposal detail */}
      <div className="ga-main">
        {selected ? (
          <>
            <div className="ga-main-header">
              <h2>{selected.title}</h2>
              {/* Document toggle removed — pipeline handles all stages */}
            </div>
            <StageStepper proposal={selected} viewStage={viewStage} onStageClick={setViewStage} />
            {showStageHints && selected && GA_STAGE_HINTS[selected.current_stage] && (
              <div className="ga-stage-hint">
                <span>{GA_STAGE_HINTS[selected.current_stage]}</span>
                <button onClick={() => { setShowStageHints(false); markGuideSeen(user?.id, 'stage_hints'); }}>&#10005; hide tips</button>
              </div>
            )}
            {showStageHints && selected && GA_STAGE_HINTS[selected.current_stage] && viewMode === 'pipeline' && (
              <div className="ga-stage-hint">
                <span>{GA_STAGE_HINTS[selected.current_stage]}</span>
                <button onClick={() => { setShowStageHints(false); markGuideSeen(user?.id, 'stage_hints'); }}>&#10005; hide tips</button>
              </div>
            )}
            {(careerStage || styleFingerprint || winPatterns) && (
              <div className="ga-career-banner">
                {careerStage && (
                  <span className="ga-career-banner-chip">
                    🎓 {careerStage === 'esi' ? 'Early Stage Investigator' : 'Established PI'}
                  </span>
                )}
                {styleFingerprint ? (
                  <span className="ga-career-banner-chip ga-career-banner-chip--voice">
                    ✍️ Voice calibrated — {styleFingerprint.proposals_analyzed || grantHistoryCount} proposal{(styleFingerprint.proposals_analyzed || grantHistoryCount) !== 1 ? 's' : ''} analyzed
                  </span>
                ) : grantHistoryCount > 0 ? (
                  <span className="ga-career-banner-chip ga-career-banner-chip--pending">
                    ✍️ {grantHistoryCount} prior proposal{grantHistoryCount !== 1 ? 's' : ''} — voice calibration on next draft
                  </span>
                ) : null}
                {winPatterns && (
                  <span className="ga-career-banner-chip ga-career-banner-chip--win" title={winPatterns.summary || ''}>
                    🏆 Win formula active — {winPatterns.funded_count}F/{winPatterns.rejected_count}R analyzed
                  </span>
                )}
              </div>
            )}
            <div className="ga-stage-content">
              {stageLoading ? (
                <div className="ga-stage-loading"><FiLoader className="spin" /> Processing…</div>
              ) : renderStage()}
            </div>
          </>
        ) : (
          <div className="ga-main-empty">
            <FiFileText className="ga-empty-icon" />
            <h3>Select a proposal</h3>
            <p>Choose a proposal from the list or start a new one.</p>
            <button className="ga-btn ga-btn--primary" onClick={() => setShowCreate(true)}>
              <FiPlus /> New Proposal
            </button>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="ga-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <h3>New Grant Proposal</h3>
            <div className="ga-form-group">
              <label>Proposal Title</label>
              <input
                autoFocus
                placeholder="e.g. UTC FY2025 — Safety & Automation"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createProposal()}
              />
            </div>
            <div className="ga-modal-actions">
              <button className="ga-btn ga-btn--outline" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="ga-btn ga-btn--primary" disabled={!newTitle.trim()} onClick={createProposal}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
